/*
 * Hoppa page SDK — injected by the server into every served page, right after
 * window.__HOPPA_SID__. Gives a generated site two things without any server code of its own:
 *   hoppa.ai.call(name, input)          → run one of the site's AI functions
 *   hoppa.store.get/set/increment/list  → the site's key-value memory (no model involved)
 *   hoppa.scores.submit/top             → a game's leaderboard: best score per player, top x
 *   hoppa.scores.nickname               → the nickname this browser used last ('' if none)
 *   hoppa.scores.shareChallenge         → share a link that challenges a friend to beat a score
 *   hoppa.scores.challenge              → the challenge in this page's link, or null
 *   hoppa.reviews()                     → the newest Google reviews of the linked business (#523)
 *   hoppa.rooms.open                    → play together, live: a room with a 4-letter code (#413)
 *   hoppa.rooms.lobby / list            → find an open room without a code, as an overlay (#448)
 *   room.chat / onChat / showChat       → chat in a room, with a ready-made panel over the game (#448)
 * And one thing for every game without asking: a key typed in a text field stays with the field, so a
 * game's WASD or arrows (Phaser listens on window) do not eat the a and d of a name (#509).
 * Errors are thrown as Error objects with a `code` (rate_limited, budget_exhausted,
 * invalid_input, unknown_function, unavailable, disabled) so a page can show its fallback.
 * A rate_limited error also carries `retryAfter` (seconds, or null) and is logged as a console.warn
 * that starts with LIMIT_PREFIX: a limit of Hoppa, not a bug in the page (#463).
 */
(function () {
  if (window.hoppa) return;

  function sid() {
    return window.__HOPPA_SID__;
  }

  var fallbackVisitorId = null;
  function visitorId() {
    try {
      var v = localStorage.getItem('hoppa_vid');
      if (!v) {
        v = randomId();
        localStorage.setItem('hoppa_vid', v);
      }
      return v;
    } catch (e) {
      // Storage blocked (private mode): stay one visitor for the life of this page
      if (!fallbackVisitorId) fallbackVisitorId = randomId();
      return fallbackVisitorId;
    }
  }

  function randomId() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    var s = '';
    for (var i = 0; i < 32; i++) s += Math.floor(Math.random() * 16).toString(16);
    return s;
  }

  // The builder reads this prefix (src/lib/runtimeLimits.ts): no repair turn, a notice with the wait
  var LIMIT_PREFIX = '[hoppa:rate_limited]';
  var LIMIT_WARN_EVERY = 10000; // a page that polls hits the limit many times a second: one line per API
  var limitWarnedAt = {};

  function apiOf(path) {
    var m = /^\/(data|scores|reviews|rooms|ai|assistant)(\/|$)/.exec(path);
    // Reviews count in the store's windows (#523): a limit on them is the store's limit. The AI assistant (#599)
    // is an AI call to the builder's notice, with windows of its own on the server
    return m ? { data: 'store', scores: 'scores', reviews: 'store', rooms: 'rooms', ai: 'ai', assistant: 'ai' }[m[1]] : 'api';
  }

  function retryAfterSeconds(res) {
    var raw = res.headers && res.headers.get ? res.headers.get('Retry-After') : null;
    var n = raw == null || String(raw).trim() === '' ? NaN : Number(raw);
    return isFinite(n) && n >= 0 ? Math.ceil(n) : null;
  }

  function warnLimit(api, error) {
    var now = Date.now();
    if (limitWarnedAt[api] && now - limitWarnedAt[api] < LIMIT_WARN_EVERY) return;
    limitWarnedAt[api] = now;
    var wait = error.retryAfter == null ? '?' : error.retryAfter;
    console.warn(LIMIT_PREFIX + ' ' + api + ' retry-after=' + wait + ' ' + error.message);
  }

  function request(method, path, body) {
    return requestUrl(method, '/api/sessions/' + sid() + path, body, apiOf(path));
  }

  // (story-411) Also for the stars, which live under /api/games/ rather than /api/sessions/
  async function requestUrl(method, url, body, api) {
    var options = {
      method: method,
      headers: { 'Content-Type': 'application/json', 'X-Hoppa-Visitor': visitorId() }
    };
    if (body !== undefined) options.body = JSON.stringify(body);

    var res;
    try {
      res = await fetch(url, options);
    } catch (e) {
      throw Object.assign(new Error('Geen verbinding'), { code: 'unavailable', status: 0 });
    }
    var data = null;
    try {
      data = await res.json();
    } catch (e) {
      /* no JSON body */
    }
    if (!res.ok) {
      // The per-IP policy answers a bare 429 without a body; still tell the page it is throttling
      var code = (data && data.code) || (res.status === 429 ? 'rate_limited' : 'unavailable');
      var message = (data && data.error) || (code === 'rate_limited' ? 'Even wachten: te veel aanvragen vanaf dit adres.' : 'Hoppa-fout (' + res.status + ')');
      // details: the whole body, for a caller that needs more than the message (the assistant's contact, #599)
      var error = Object.assign(new Error(message), { code: code, status: res.status, details: data });
      if (code === 'rate_limited') {
        error.retryAfter = retryAfterSeconds(res);
        warnLimit(api || 'api', error);
      }
      throw error;
    }
    return data;
  }

  function enc(s) {
    return encodeURIComponent(String(s));
  }

  // The nickname is remembered per browser, so the end screen can fill it in next time
  var fallbackNickname = '';
  var nicknameInMemory = false; // set once a write failed: from then on memory is newer than storage
  function rememberedNickname() {
    if (nicknameInMemory) return fallbackNickname;
    try {
      return localStorage.getItem('hoppa_nickname') || '';
    } catch (e) {
      return fallbackNickname;
    }
  }
  function rememberNickname(name) {
    fallbackNickname = name || '';
    try {
      localStorage.setItem('hoppa_nickname', fallbackNickname);
      nicknameInMemory = false;
    } catch (e) {
      // Storage blocked or full: keep it for this page
      nicknameInMemory = true;
    }
  }

  // The room this tab is in (#448, "je moet kunnen F5-en"): code and player id in sessionStorage, which a
  // reload keeps and a new tab does not. lobby() takes you back in as the same player; the room hands the
  // chat back in its welcome. Gone after room.close() or when the room no longer exists.
  var ROOM_MEMORY_MS = 6 * 60 * 60 * 1000;
  function roomMemoryKey() {
    return 'hoppa_room_' + sid();
  }
  function rememberedRoom() {
    try {
      var saved = JSON.parse(sessionStorage.getItem(roomMemoryKey()) || 'null');
      if (!saved || !ROOM_CODE.test(saved.code) || !PLAYER_ID.test(saved.id)) return null;
      if (!(Date.now() - saved.at < ROOM_MEMORY_MS)) return null;
      return saved;
    } catch (e) {
      return null;
    }
  }
  function rememberRoom(code, id) {
    try {
      sessionStorage.setItem(roomMemoryKey(), JSON.stringify({ code: code, id: id, at: Date.now() }));
    } catch (e) {
      /* no storage: F5 is a new start */
    }
  }
  function forgetRoom(code) {
    try {
      var saved = rememberedRoom();
      if (!code || !saved || saved.code === code) sessionStorage.removeItem(roomMemoryKey());
    } catch (e) {
      /* nothing to forget */
    }
  }

  // A challenge lives in the link only (#298): ?uitdaging=<name>&score=<n>&bord=<board>[&laag=1].
  // Nothing of it goes to the server or onto a leaderboard. Anyone can type such a link, so the
  // name is cut back to what a nickname may be; a page shows it with textContent like any name.
  var CHALLENGE_PARAMS = ['uitdaging', 'score', 'bord', 'laag'];
  var BOARD_NAME = /^[a-z0-9][a-z0-9_-]{0,39}$/; // as SiteDataRules on the server

  var UNICODE = (function () {
    try {
      return {
        drop: new RegExp("[^\\p{L}\\p{M}\\p{N}\\s\\-_.!?']", 'gu'),
        marks: new RegExp('(\\p{M}{2})\\p{M}+', 'gu'), // "zalgo": two marks per letter, as NicknamePolicy
        letterOrDigit: new RegExp('[\\p{L}\\p{N}]', 'u')
      };
    } catch (e) {
      return null; // a browser without Unicode property escapes: Latin letters only
    }
  })();

  function cleanName(name) {
    var s = String(name == null ? '' : name);
    if (UNICODE) s = s.normalize('NFC').replace(UNICODE.drop, '').replace(UNICODE.marks, '$1');
    else s = s.replace(/[^A-Za-z0-9À-ɏ\s\-_.!?']/g, '');
    s = Array.from(s.replace(/\s+/g, ' ').trim()).slice(0, 16).join('').trim();
    // A name without a letter or digit ("...", a lone accent) is no name, as on the leaderboard
    var letterOrDigit = UNICODE ? UNICODE.letterOrDigit : /[A-Za-z0-9À-ɏ]/;
    return letterOrDigit.test(s) ? s : '';
  }

  function readChallenge() {
    var params;
    try {
      params = new URLSearchParams(location.search);
    } catch (e) {
      return null;
    }
    var name = cleanName(params.get('uitdaging'));
    var rawScore = params.get('score');
    var score = rawScore == null || rawScore.trim() === '' ? NaN : Number(rawScore);
    if (!name || !isFinite(score)) return null;
    var board = params.get('bord');
    var lowestWins = params.get('laag') === '1';
    return {
      name: name,
      score: score,
      board: board && BOARD_NAME.test(board) ? board : null,
      lowestWins: lowestWins,
      // 'won' only when the challenge is beaten, so an equal score is a 'tie', not a win
      outcome: function (myScore) {
        var mine = Number(myScore);
        if (!isFinite(mine)) return 'lost';
        if (mine === score) return 'tie';
        return (lowestWins ? mine < score : mine > score) ? 'won' : 'lost';
      }
    };
  }

  function challengeUrl(board, name, score, lowestWins) {
    var url = new URL(location.href);
    CHALLENGE_PARAMS.forEach(function (p) {
      url.searchParams.delete(p);
    });
    url.hash = '';
    url.searchParams.set('uitdaging', name);
    url.searchParams.set('score', String(score));
    url.searchParams.set('bord', board);
    if (lowestWins) url.searchParams.set('laag', '1');
    return url.toString();
  }

  // A phone shares through its share sheet; a desktop copies, even where it has a share dialog
  function isPhone() {
    try {
      return !!navigator.share && matchMedia('(pointer: coarse)').matches;
    } catch (e) {
      return false;
    }
  }

  async function copyText(text) {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (e) {
      /* no permission (an iframe without clipboard-write): try the old way */
    }
    try {
      var area = document.createElement('textarea');
      area.value = text;
      area.setAttribute('readonly', '');
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      var ok = document.execCommand('copy');
      area.remove();
      return ok;
    } catch (e) {
      return false;
    }
  }

  function scopeQuery(options) {
    return '?scope=' + enc((options && options.scope) || 'visitor');
  }

  // ─── Samen spelen: hoppa.rooms (#413) ─────────────────────────────────────────────────────
  // A room is a 4-letter code on the server; every player holds one WebSocket to it and the server
  // passes messages on. The game sees six things: open, code/link, track, onPlayer, emit/on, isHost.
  // Everything else (the ticket, the socket, reconnecting, re-creating the room after a deploy,
  // sending 15×/s, interpolation) stays in here.
  var ROOM_CODE = /^[A-HJ-NP-Z]{4}$/; // as RoomCode on the server: no I, no O
  var TRACK_INTERVAL = 1000 / 15;
  var TRACK_HEARTBEAT = 1000; // an idle player still shows up for a newcomer
  var MAX_MESSAGE = 1000; // bytes (UTF-8, as the server counts); the server takes 1 KB
  var OPEN_TIMEOUT = 8000;
  var PING_INTERVAL = 20000;
  var PONG_TIMEOUT = 8000;
  // The server allows 30 messages a second per player and track takes 15: events are metered here,
  // what goes over waits in the queue and leaves in order, so nothing is ever dropped
  var EMIT_PER_SECOND = 12;
  var QUEUE_LIMIT = 500;
  // Chat (#448): free text, no word filter; only what the server also enforces
  var CHAT_MAX = 200; // characters as people count them (an emoji is one)
  var CHAT_PER_SECOND = 2;
  var CHAT_WINDOW = 1100; // a little over the server's one-second window, so network jitter does not cut a line
  var CHAT_LOG = 50; // lines the room keeps, as Rooms:ChatHistory on the server: welcome hands them back after F5
  var PLAYER_ID = /^[A-Za-z0-9_-]{6,32}$/; // as RoomRegistry.IsPlayerId

  function roomError(code, message) {
    return Object.assign(new Error(message), { code: code });
  }

  function byteLength(text) {
    try {
      return new TextEncoder().encode(text).length;
    } catch (e) {
      return text.length;
    }
  }

  function roomCodeFromLink() {
    try {
      return new URLSearchParams(location.search).get('room');
    } catch (e) {
      return null;
    }
  }

  // From Hoppa Games (#448): "Speel mee" on the platform opens the game as ?room=KIP4&naam=Lisa. The name is
  // remembered like one typed in the lobby and leaves the address bar (a link someone shares should not
  // carry it); lobby() then joins that room by itself, without its screen. A game that calls open() gets
  // the room and the name the same way. &speler= is the id the watcher had on Hoppa Games: the player takes
  // it over, so what they said while watching is theirs in the chat here too.
  var fromPlatform = (function () {
    var params;
    try {
      params = new URLSearchParams(location.search);
    } catch (e) {
      return false;
    }
    var raw = params.get('naam');
    if (raw == null) return false;
    var name = cleanName(raw);
    if (name) rememberNickname(name);
    var code = String(params.get('room') || '').toUpperCase();
    var id = params.get('speler');
    if (ROOM_CODE.test(code) && id && PLAYER_ID.test(id)) rememberRoom(code, id);
    try {
      var url = new URL(location.href);
      url.searchParams.delete('naam');
      url.searchParams.delete('speler');
      history.replaceState(history.state, '', url.toString());
    } catch (e) {
      /* the name stays in the bar; harmless */
    }
    return !!name && !!params.get('room');
  })();

  function roomLink(code) {
    var url = new URL(location.href);
    url.searchParams.set('room', code);
    url.hash = '';
    return url.toString();
  }

  // One line of at most CHAT_MAX characters, as ChatText on the server; '' when nothing is left
  function cleanChat(text) {
    var s = String(text == null ? '' : text)
      .replace(/[\u0000-\u001f\u007f-\u009f]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    var chars = Array.from(s);
    return chars.length > CHAT_MAX ? chars.slice(0, CHAT_MAX).join('').trim() : s;
  }

  function createRoom(options) {
    options = options || {};
    var raw = options.code != null ? String(options.code) : roomCodeFromLink();
    var code = raw == null || String(raw).trim() === '' ? null : String(raw).replace(/\s+/g, '').toUpperCase();
    if (code !== null && !ROOM_CODE.test(code)) throw roomError('invalid_input', 'Een kamercode is vier letters.');
    var name = cleanName(options.name != null ? options.name : rememberedNickname()) || 'Speler';
    // Open rooms are listed in the lobby of this game (#448); a private one only takes its code
    var isPrivate = !!options.private;
    var mustExist = !!options.existing && code !== null;

    // Back in the room this tab was in (F5): the same player id, so the others see no one leave and come
    // back, and our own lines in the chat that comes back are ours
    var memory = code !== null ? rememberedRoom() : null;
    var me = { id: memory && memory.code === code ? memory.id : 'p' + randomId().replace(/-/g, '').slice(0, 11), name: name };
    var players = []; // { id, name }: one object per player id, so a game can hang its sprite on it
    var watchers = []; // { id, name, watcher: true }: on Hoppa Games, reading and writing the chat (#448)
    var host = null;
    var welcomed = false;
    var closed = false;
    var ws = null;
    var seq = 0;
    var trackFn = null;
    var lastTrackJson = null;
    var lastTrackAt = 0;
    var queue = []; // emits that wait: for the socket, or for the next second's budget; always in order
    var emitBudget = EMIT_PER_SECOND;
    var shadows = {}; // player id → { prev, next, prevAt, nextAt, seq, view }
    var handlers = { join: [], leave: [], host: [], player: [], chat: [], events: {} };
    var muted = {}; // player id → true: their chat stays out of this page
    var chatSent = []; // when our last chat lines left, for CHAT_PER_SECOND
    var chatLog = []; // { player, text, at }: the last CHAT_LOG lines, also those from before F5
    var lastChatNumber = 0; // the room numbers its lines: after a reconnect only what we missed
    var chatPanel = null;
    var retry = 0;
    var reconnectTimer = null;
    var pingTimer = null;
    var pongTimer = null;
    var frame = null;
    var warnedSize = false;
    var maxPlayers = 4; // from welcome
    var openResolve = null;
    var openReject = null;
    var openDeadline = Date.now() + OPEN_TIMEOUT;

    function fire(list, a, b) {
      for (var i = 0; i < list.length; i++) {
        try {
          list[i](a, b);
        } catch (e) {
          console.error('hoppa.rooms handler', e);
        }
      }
    }

    function findPlayer(id) {
      for (var i = 0; i < players.length; i++) if (players[i].id === id) return players[i];
      return null;
    }

    function findWatcher(id) {
      for (var i = 0; i < watchers.length; i++) if (watchers[i].id === id) return watchers[i];
      return null;
    }

    // Who said a line: a player, a watcher on Hoppa Games, ourselves when the id is ours. Someone the page
    // does not know only with a name: live that is a watcher (no player: the line carries the name), in the
    // chat from before F5 someone who may have gone since
    function talkerOf(line, live) {
      if (!line || !line.from) return null;
      if (line.from === me.id) return me;
      var known = findPlayer(line.from) || findWatcher(line.from);
      if (known) return known;
      if (!line.name) return null;
      var stranger = { id: String(line.from), name: String(line.name) };
      if (live) stranger.watcher = true;
      return stranger;
    }

    function logChat(player, text, at) {
      chatLog.push({ player: player, text: text, at: at });
      if (chatLog.length > CHAT_LOG) chatLog.shift();
    }

    function send(obj) {
      if (!ws || ws.readyState !== 1) return false;
      ws.send(JSON.stringify(obj));
      return true;
    }

    // Events leave in order, at most EMIT_PER_SECOND a second, and only once the room welcomed us.
    // Chat lines in that queue also keep to CHAT_PER_SECOND in any window of CHAT_WINDOW: what was said
    // during a reconnect does not leave in one burst that the server would cut (review #468)
    var chatLeft = []; // when the last chat lines left the queue
    var chatTimer = null;
    function drainQueue() {
      while (queue.length > 0 && welcomed && emitBudget > 0 && ws && ws.readyState === 1) {
        if (queue[0].t === 'chat') {
          var now = Date.now();
          chatLeft = chatLeft.filter(function (t) {
            return now - t < CHAT_WINDOW;
          });
          if (chatLeft.length >= CHAT_PER_SECOND) {
            clearTimeout(chatTimer);
            chatTimer = setTimeout(drainQueue, CHAT_WINDOW - (now - chatLeft[0]));
            return;
          }
        }
        if (!send(queue[0])) return;
        if (queue[0].t === 'chat') chatLeft.push(Date.now());
        queue.shift();
        emitBudget--;
      }
    }
    var emitTimer = setInterval(function () {
      emitBudget = EMIT_PER_SECOND;
      drainQueue();
    }, 1000);

    // ── connecting ──

    async function connect() {
      if (closed) return;
      var ticket;
      try {
        ticket = (await request('POST', '/rooms/ticket')).ticket;
      } catch (e) {
        // Refused for good (no such site, not from the site itself): stop. Otherwise try again
        if (e.code === 'unavailable' || e.code === 'rate_limited') return failed(e);
        return fatal(e);
      }
      if (closed) return;
      var proto = location.protocol === 'https:' ? 'wss://' : 'ws://';
      var socket = new WebSocket(proto + location.host + '/api/sessions/' + sid() + '/rooms/socket?ticket=' + enc(ticket));
      ws = socket;
      socket.onopen = function () {
        // After a deploy the first one back re-creates the room: as open or private as it was. Joining from the
        // lobby (a listed room, a typed code, an invitation) says `existing` the first time: a code nobody has
        // is then room_gone, not a new open room that only looks like a joined one (review #468)
        var hello = { t: 'hello', code: code, id: me.id, name: me.name, private: isPrivate };
        if (mustExist && !welcomed) hello.existing = true;
        send(hello);
      };
      socket.onmessage = function (ev) {
        var msg;
        try {
          msg = JSON.parse(ev.data);
        } catch (e) {
          return;
        }
        if (socket === ws) handle(msg);
      };
      socket.onclose = function () {
        if (socket !== ws) return;
        ws = null;
        stopPing();
        if (!closed) failed(roomError('unavailable', 'Verbinding weg'));
      };
      socket.onerror = function () {
        /* onclose follows */
      };
    }

    function failed(error) {
      if (closed) return;
      if (!welcomed && Date.now() > openDeadline) return fatal(error);
      var delay = Math.min(10000, 500 * Math.pow(2, retry++)) * (0.7 + Math.random() * 0.6);
      clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(connect, delay);
    }

    function fatal(error) {
      if (!welcomed && openReject) {
        var reject = openReject;
        openReject = openResolve = null;
        room.close();
        reject(error);
      } else {
        console.warn('hoppa.rooms: ' + error.message);
        room.close();
      }
    }

    function reconnectNow() {
      if (closed || ws) return;
      clearTimeout(reconnectTimer);
      retry = 0;
      connect();
    }

    // ── keeping the socket alive and honest ──

    function startPing() {
      stopPing();
      pingTimer = setInterval(function () {
        if (!send({ t: 'ping' })) return;
        clearTimeout(pongTimer);
        pongTimer = setTimeout(function () {
          // No answer: the connection is dead even if the browser has not noticed (a phone changed network)
          if (ws) ws.close();
        }, PONG_TIMEOUT);
      }, PING_INTERVAL);
    }

    function stopPing() {
      clearInterval(pingTimer);
      clearTimeout(pongTimer);
      pingTimer = pongTimer = null;
    }

    // ── incoming ──

    function handle(msg) {
      switch (msg.t) {
        case 'welcome':
          return welcome(msg);
        case 'join': {
          if (!msg.p || msg.p.id === me.id || findPlayer(msg.p.id)) return;
          var joined = { id: msg.p.id, name: String(msg.p.name || 'Speler') };
          players.push(joined);
          fire(handlers.join, joined);
          if (chatPanel) chatPanel.note(joined.name + ' doet mee');
          return;
        }
        case 'leave': {
          var left = msg.p && findPlayer(msg.p.id);
          if (!left) return;
          players.splice(players.indexOf(left), 1);
          delete shadows[left.id];
          fire(handlers.leave, left);
          if (chatPanel) chatPanel.note(left.name + ' is weg');
          return;
        }
        case 'watch': {
          if (!msg.p || findWatcher(msg.p.id)) return;
          var watcher = { id: msg.p.id, name: String(msg.p.name || 'Speler'), watcher: true };
          watchers.push(watcher);
          if (chatPanel) chatPanel.note(watcher.name + ' kijkt mee op Hoppa Games');
          return;
        }
        case 'unwatch': {
          var stopped = msg.p && findWatcher(msg.p.id);
          if (stopped) watchers.splice(watchers.indexOf(stopped), 1);
          if (chatPanel) chatPanel.refresh();
          return;
        }
        case 'chat': {
          if (typeof msg.n === 'number' && msg.n > lastChatNumber) lastChatNumber = msg.n;
          var talker = talkerOf(msg, true);
          if (!talker || typeof msg.x !== 'string') return;
          logChat(talker, msg.x, Date.now());
          if (muted[talker.id]) return;
          fire(handlers.chat, talker, msg.x);
          if (chatPanel) chatPanel.line(talker, msg.x);
          return;
        }
        case 'host':
          return setHost(msg.id);
        case 'track': {
          var from = findPlayer(msg.from);
          if (!from || !msg.s) return;
          var shadow = shadows[from.id] || (shadows[from.id] = { prev: null, next: null, prevAt: 0, nextAt: 0, seq: -1, view: {} });
          var n = typeof msg.seq === 'number' ? msg.seq : 0;
          if (n <= shadow.seq) return; // an older position that arrived late
          shadow.seq = n;
          shadow.prev = shadow.next;
          shadow.prevAt = shadow.nextAt;
          shadow.next = msg.s;
          shadow.nextAt = performance.now();
          return;
        }
        case 'emit': {
          var sender = findPlayer(msg.from);
          if (!sender || typeof msg.n !== 'string') return;
          fire(handlers.events[msg.n] || [], msg.d, sender);
          return;
        }
        case 'pong':
          clearTimeout(pongTimer);
          return;
        case 'error':
          // The server closes after this; room_full and room_limit are final, the rest we retry on close
          if (msg.code === 'room_full' || msg.code === 'room_gone' || msg.code === 'room_limit' || msg.code === 'invalid_input') {
            fatal(roomError(msg.code, msg.message || 'Samen spelen lukt niet'));
          } else if (msg.code === 'chat_limited') {
            if (chatPanel) chatPanel.note(msg.message || 'Even rustig aan met chatten.');
          } else {
            console.warn('hoppa.rooms: ' + (msg.message || msg.code)); // too_big, rate_limited: the game sends too much
          }
          return;
      }
    }

    function welcome(msg) {
      code = msg.code;
      if (typeof msg.open === 'boolean') isPrivate = !msg.open; // who joined by code now knows too
      if (typeof msg.maxPlayers === 'number') maxPlayers = msg.maxPlayers;
      me.name = msg.me && msg.me.name ? String(msg.me.name) : me.name;
      var incoming = (msg.players || []).filter(function (p) {
        return p && p.id !== me.id;
      });
      // After a reconnect: who is new, who is gone, keeping the objects of those who stayed
      var stayed = [];
      incoming.forEach(function (p) {
        var known = findPlayer(p.id);
        if (known) {
          known.name = String(p.name || known.name);
          stayed.push(known);
        }
      });
      var gone = players.filter(function (p) {
        return stayed.indexOf(p) < 0;
      });
      players = stayed;
      gone.forEach(function (p) {
        delete shadows[p.id];
        fire(handlers.leave, p);
      });
      incoming.forEach(function (p) {
        if (findPlayer(p.id)) return;
        var joined = { id: p.id, name: String(p.name || 'Speler') };
        players.push(joined);
        fire(handlers.join, joined);
      });
      watchers = (msg.watchers || []).filter(function (p) {
        return p && p.id;
      }).map(function (p) {
        return findWatcher(p.id) || { id: p.id, name: String(p.name || 'Speler'), watcher: true };
      });
      var first = !welcomed;
      welcomed = true;
      retry = 0;
      rememberRoom(code, me.id);
      setHost(msg.host, first);
      // The chat the room kept: everything after F5, after a reconnect only what we missed. Our own lines
      // from this page are already on screen; those from before F5 are not
      (Array.isArray(msg.chat) ? msg.chat : []).forEach(function (line) {
        if (!line || typeof line.x !== 'string' || typeof line.n !== 'number' || line.n <= lastChatNumber) return;
        lastChatNumber = line.n;
        if (!first && line.from === me.id) return;
        var talker = talkerOf(line);
        if (!talker) return;
        logChat(talker, line.x, typeof line.at === 'number' ? line.at : Date.now());
        if (chatPanel && !muted[talker.id]) chatPanel.line(talker, line.x, true);
      });
      if (chatPanel) chatPanel.refresh();
      startPing();
      // What was said while we were away goes out now, in order
      drainQueue();
      if (first && openResolve) {
        var resolve = openResolve;
        openResolve = openReject = null;
        resolve(room);
      }
    }

    function setHost(id, silent) {
      var was = host === me.id;
      host = id;
      var now = host === me.id;
      if (!silent && was !== now) fire(handlers.host, now);
    }

    // ── outgoing: positions 15×/s, the newest wins ──

    var trackTimer = setInterval(function () {
      if (!trackFn || !ws || ws.readyState !== 1 || !welcomed) return;
      var state;
      try {
        state = trackFn();
      } catch (e) {
        console.error('hoppa.rooms track', e);
        return;
      }
      if (state == null) return;
      var json = JSON.stringify({ t: 'track', seq: ++seq, s: state });
      var size = byteLength(json);
      if (size > MAX_MESSAGE) {
        if (!warnedSize) console.warn('hoppa.rooms: track() geeft te veel terug (' + size + ' bytes, hooguit ' + MAX_MESSAGE + ')');
        warnedSize = true;
        return;
      }
      var now = Date.now();
      // Unchanged: once a second is enough, so an idle player still appears for whoever just joined
      if (json.slice(json.indexOf('"s"')) === lastTrackJson && now - lastTrackAt < TRACK_HEARTBEAT) return;
      lastTrackJson = json.slice(json.indexOf('"s"'));
      lastTrackAt = now;
      ws.send(json);
    }, TRACK_INTERVAL);

    // ── onPlayer: interpolated positions, every frame ──

    function tick() {
      frame = null;
      if (closed || handlers.player.length === 0) return;
      var now = performance.now();
      for (var i = 0; i < players.length; i++) {
        var p = players[i];
        var sh = shadows[p.id];
        if (!sh || !sh.next) continue;
        var view = sh.view;
        var span = sh.prev ? Math.min(250, Math.max(33, sh.nextAt - sh.prevAt)) : 0;
        var t = sh.prev ? Math.min(1, (now - sh.nextAt) / span) : 1;
        for (var key in sh.next) {
          var b = sh.next[key];
          var a = sh.prev ? sh.prev[key] : undefined;
          view[key] = typeof b === 'number' && typeof a === 'number' ? a + (b - a) * t : b;
        }
        for (var old in view) if (!(old in sh.next)) delete view[old];
        fire(handlers.player, p, view);
      }
      frame = requestAnimationFrame(tick);
    }

    function startFrames() {
      if (frame === null && !closed) frame = requestAnimationFrame(tick);
    }

    function onVisible() {
      if (document.visibilityState === 'visible') {
        reconnectNow();
        startFrames();
      }
    }
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', reconnectNow);

    // ── what the game sees ──

    var room = {
      get code() {
        return code;
      },
      get link() {
        return code ? roomLink(code) : null;
      },
      get isHost() {
        return host === me.id;
      },
      get me() {
        return me;
      },
      get players() {
        return players.concat([me]);
      },
      // Who reads along on Hoppa Games (#448): { id, name, watcher: true }; they chat, they do not play
      get watchers() {
        return watchers.slice();
      },
      get closed() {
        return closed;
      },
      // Private rooms stay out of the lobby; only the code (or the link) gets you in
      get isPrivate() {
        return isPrivate;
      },
      get maxPlayers() {
        return maxPlayers;
      },
      // The function is read 15× a second; return a small object (x, y, anim, flip): numbers are interpolated
      track: function (fn) {
        trackFn = typeof fn === 'function' ? fn : null;
      },
      // cb(player, state) every frame for every other player with a position; state is already smoothed
      onPlayer: function (cb) {
        if (typeof cb === 'function') handlers.player.push(cb);
        startFrames();
      },
      // Reliable and in order, also to your own `on` handlers, so one code path handles everyone.
      // Metered (EMIT_PER_SECOND): a burst arrives a little later at the others, never dropped
      emit: function (name, data) {
        if (typeof name !== 'string' || !name) throw roomError('invalid_input', 'emit(naam, data): de naam ontbreekt');
        var msg = { t: 'emit', n: name, d: data === undefined ? null : data };
        if (byteLength(JSON.stringify(msg)) > MAX_MESSAGE) throw roomError('invalid_input', 'emit: hooguit ' + MAX_MESSAGE + ' bytes per gebeurtenis');
        if (!closed) {
          if (queue.length >= QUEUE_LIMIT) {
            console.warn('hoppa.rooms: te veel gebeurtenissen achter elkaar, de oudste vervalt');
            queue.shift();
          }
          queue.push(msg);
          drainQueue();
        }
        fire(handlers.events[name] || [], msg.d, me);
      },
      on: function (name, cb) {
        if (typeof cb !== 'function') return;
        (handlers.events[name] || (handlers.events[name] = [])).push(cb);
      },
      onJoin: function (cb) {
        if (typeof cb === 'function') handlers.join.push(cb);
      },
      onLeave: function (cb) {
        if (typeof cb === 'function') handlers.leave.push(cb);
      },
      // cb(isHost) when the host changes: the first player is host, after that the oldest who is left
      onHost: function (cb) {
        if (typeof cb === 'function') handlers.host.push(cb);
      },
      // Chat (#448): one line of free text to everyone in the room; also to your own onChat handlers.
      // At most CHAT_PER_SECOND lines a second (more is dropped: false), CHAT_MAX characters. The room keeps the last lines for F5 (chatLog).
      chat: function (text) {
        var line = cleanChat(text);
        if (!line || closed) return false;
        var now = Date.now();
        chatSent = chatSent.filter(function (t) {
          return now - t < 1000;
        });
        if (chatSent.length >= CHAT_PER_SECOND) return false;
        chatSent.push(now);
        if (queue.length < QUEUE_LIMIT) {
          queue.push({ t: 'chat', x: line });
          drainQueue();
        }
        logChat(me, line, now);
        fire(handlers.chat, me, line);
        if (chatPanel) chatPanel.line(me, line);
        return true;
      },
      // cb(player, text) for every chat line, yours too; never for a player this page muted. Lines from
      // before this page (F5) do not come through here: they are in chatLog
      onChat: function (cb) {
        if (typeof cb === 'function') handlers.chat.push(cb);
      },
      // The last lines of this room, oldest first, also those from before F5: [{ player, text, at }]
      get chatLog() {
        return chatLog.filter(function (l) {
          return !muted[l.player.id];
        });
      },
      // The ready-made chat panel over the game: collapsible, players to mute, quick lines on a phone
      showChat: function (opts) {
        if (closed) return;
        if (!chatPanel) chatPanel = createChatPanel(room, opts || {});
        chatPanel.show();
      },
      hideChat: function () {
        if (chatPanel) chatPanel.hide();
      },
      // Mute a player on this page only: their chat no longer shows or reaches onChat
      mute: function (player, on) {
        var id = player && player.id ? player.id : player;
        if (!id || id === me.id) return;
        if (on === false) delete muted[id];
        else muted[id] = true;
        if (chatPanel) chatPanel.refresh();
      },
      isMuted: function (player) {
        return !!muted[player && player.id ? player.id : player];
      },
      close: function () {
        if (closed) return;
        closed = true;
        forgetRoom(code); // left on purpose: F5 no longer brings you back
        if (chatPanel) chatPanel.destroy();
        chatPanel = null;
        clearInterval(trackTimer);
        clearInterval(emitTimer);
        clearTimeout(chatTimer);
        clearTimeout(reconnectTimer);
        stopPing();
        if (frame !== null) cancelAnimationFrame(frame);
        document.removeEventListener('visibilitychange', onVisible);
        window.removeEventListener('online', reconnectNow);
        var socket = ws;
        ws = null;
        if (socket) {
          try {
            socket.close(1000, 'bye');
          } catch (e) {
            /* already gone */
          }
        }
      }
    };

    return new Promise(function (resolve, reject) {
      openResolve = resolve;
      openReject = reject;
      connect();
    });
  }

  // ─── Lobby en chat als overlay (#448) ──────────────────────────────────────────────────────
  // Ready-made UI, so a game never builds its own lobby or chat: one call each. Everything lives in a
  // shadow root on a fixed host element, so the game's CSS does not reach in and ours does not leak
  // out; key and pointer events stop at that host, so typing a "w" in the chat does not walk the
  // player and a tap on a button does not jump. Names and chat lines only ever go in as textContent.

  // The look of Hoppa Games (#448, the human on #468): Fredoka, sun yellow on deep purple, mint, coral and
  // sky for the players. Always dark, as games.hoppa.site; the same panel as on the platform.
  var FONT_URL = 'https://fonts.googleapis.com/css2?family=Fredoka:wght@400;500;600;700&display=swap';
  var PLAYER_COLORS = ['#4ecdc4', '#ff6b6b', '#5dade2', '#b28dff', '#ff9f43', '#7bd88f', '#ff8fc7', '#ffd23f'];

  var UI_CSS =
    '.hp{--bg:#1e1538;--surface:#2a1f4d;--surface-2:#352a5e;--surface-3:#41356f;--ink:#fff8ec;--muted:#b3a6d4;--line:#3f3270;' +
    '--brand:#ffd23f;--brand-hover:#ffc400;--on-brand:#2a1f0a;--accent:#4ecdc4;--danger:#ff8b8b;--glass:rgba(42,31,77,.97);' +
    '--shadow:0 16px 44px rgba(0,0,0,.45);--display:"Fredoka","Figtree",system-ui,sans-serif;' +
    'font:15px/1.4 "Fredoka","Figtree",system-ui,-apple-system,"Segoe UI",Roboto,Arial,sans-serif;color:var(--ink);color-scheme:dark;' +
    '-webkit-font-smoothing:antialiased;-webkit-text-size-adjust:100%;text-align:left}' +
    '.hp *{box-sizing:border-box}' +
    ':where(.hp) button{font:inherit;color:inherit;cursor:pointer;border:0;margin:0;padding:0;background:none;-webkit-tap-highlight-color:transparent}' +
    ':where(.hp) :is(h2,p,ul){margin:0;padding:0}' +
    '.hp :focus-visible{outline:3px solid var(--accent);outline-offset:2px}' +
    '.btn{display:inline-flex;align-items:center;justify-content:center;gap:6px;min-height:46px;padding:0 18px;border-radius:999px;font-family:var(--display);font-weight:600;font-size:16px;white-space:nowrap;transition:transform .12s,background .12s}' +
    '.btn:active{transform:scale(.97)}' +
    '.btn.primary{background:var(--brand);color:var(--on-brand);box-shadow:0 6px 18px rgba(255,210,63,.28)}.btn.primary:hover{background:var(--brand-hover)}' +
    '.btn.ghost{background:var(--surface-2);color:var(--ink)}.btn.ghost:hover{background:var(--surface-3)}.btn:disabled{opacity:.55;cursor:default}' +
    '.field{width:100%;min-height:46px;padding:0 16px;border:2px solid var(--line);border-radius:999px;background:var(--bg);color:var(--ink);font:inherit;font-size:16px}' +
    '.field:focus{border-color:var(--brand);outline:none}.field::placeholder{color:var(--muted);opacity:1}' +
    '.hidden{display:none!important}' +
    '.ava{flex:none;display:inline-grid;place-items:center;width:30px;height:30px;border-radius:50%;color:#1e1538;font:700 14px/1 var(--display);text-transform:uppercase}' +
    '.ava.sm{width:22px;height:22px;font-size:11px}' +
    // lobby
    '.backdrop{position:fixed;top:0;right:0;bottom:0;left:0;display:flex;align-items:center;justify-content:center;padding:16px;' +
    'background:rgba(12,8,28,.7);pointer-events:auto}' +
    '.card{position:relative;width:100%;max-width:440px;max-height:100%;overflow-y:auto;padding:22px;border-radius:28px;background:var(--surface);' +
    'border:2px solid var(--line);box-shadow:var(--shadow);overscroll-behavior:contain;' +
    'background-image:radial-gradient(circle at 100% 0,rgba(78,205,196,.16),transparent 40%),radial-gradient(circle at 0 100%,rgba(255,107,107,.12),transparent 45%)}' +
    '.card:focus{outline:none}' +
    '.top{display:flex;align-items:flex-start;gap:12px;margin-bottom:16px}.top>div{min-width:0}' +
    '.top h2{font:700 26px/1.15 var(--display);color:var(--brand)}' +
    '.sub{margin-top:2px;color:var(--muted);font-size:14px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}' +
    '.close{margin-left:auto;flex:none;width:42px;height:42px;border-radius:50%;background:var(--surface-2);display:grid;place-items:center}.close:hover{background:var(--surface-3)}' +
    '.close svg{width:20px;height:20px}' +
    '.label{display:block;margin:0 0 8px;font:600 13px/1.2 var(--display);color:var(--muted);text-transform:uppercase;letter-spacing:.12em}' +
    '.section{margin-top:18px}' +
    '.rooms{list-style:none;display:flex;flex-direction:column;gap:8px}' +
    '.room{display:flex;align-items:center;gap:10px;padding:8px 8px 8px 12px;border:2px solid var(--line);border-radius:20px;background:var(--bg)}' +
    '.room .who{flex:1;min-width:0}.room strong{display:block;font:600 16px/1.3 var(--display);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}' +
    '.meta{font-size:13px;color:var(--muted)}' +
    '.empty{padding:14px;border:2px dashed var(--line);border-radius:20px;color:var(--muted);font-size:14px;text-align:center}' +
    '.row{display:flex;gap:8px;align-items:center}.row .field{flex:1;min-width:0}' +
    '.code{text-transform:uppercase;letter-spacing:.3em;font-weight:700}' +
    '.check{display:flex;align-items:center;gap:8px;margin-top:10px;font-size:14px;color:var(--muted);cursor:pointer}' +
    '.check input{width:20px;height:20px;margin:0;accent-color:var(--brand)}' +
    '.invite{display:flex;align-items:center;gap:10px;padding:12px 12px 12px 16px;border-radius:20px;background:rgba(255,210,63,.12);border:2px solid rgba(255,210,63,.4);margin-bottom:4px}' +
    '.invite p{flex:1;min-width:0;font-weight:600}' +
    '.error{min-height:20px;margin-top:12px;color:var(--danger);font-size:14px;font-weight:600}' +
    '.solo{display:block;margin:4px auto 0;min-height:40px;padding:0 12px;color:var(--muted);text-decoration:underline;text-underline-offset:3px}' +
    '.bigcode{margin:14px 0;font:700 52px/1 var(--display);letter-spacing:.2em;text-align:center;color:var(--brand)}' +
    '.stack{display:flex;flex-direction:column;gap:8px}.lead{color:var(--muted)}' +
    '@media (max-width:480px){.backdrop{padding:12px;align-items:flex-start}.card{padding:18px;border-radius:24px}.top h2{font-size:22px}}' +
    // chat
    '.chat{position:fixed;display:flex;flex-direction:column;align-items:flex-end;gap:8px;pointer-events:none;max-width:calc(100vw - 20px)}' +
    '.chat.tr,.chat.tl{top:calc(10px + env(safe-area-inset-top,0px))}.chat.br,.chat.bl{bottom:calc(10px + env(safe-area-inset-bottom,0px));flex-direction:column-reverse}' +
    '.chat.tr,.chat.br{right:10px}.chat.tl,.chat.bl{left:10px;align-items:flex-start}' +
    '.toggle{pointer-events:auto;position:relative;width:52px;height:52px;border-radius:50%;background:var(--brand);color:var(--on-brand);box-shadow:var(--shadow);display:grid;place-items:center}' +
    '.toggle svg{width:24px;height:24px}' +
    '.badge{position:absolute;top:-4px;right:-4px;min-width:22px;height:22px;padding:0 6px;border-radius:11px;background:#ff6b6b;color:#fff;font:700 12px/22px var(--display);text-align:center;box-shadow:0 0 0 2px var(--bg)}' +
    '.toast{display:flex;align-items:center;gap:8px;max-width:min(300px,calc(100vw - 20px));padding:6px 14px 6px 6px;border-radius:999px;background:var(--glass);border:2px solid var(--line);box-shadow:var(--shadow);font-size:14px;overflow:hidden;white-space:nowrap}' +
    '.toast span{overflow:hidden;text-overflow:ellipsis}.toast.plain{padding-left:14px}' +
    '.panel{pointer-events:auto;width:340px;max-width:calc(100vw - 20px);max-height:min(62vh,460px);display:flex;flex-direction:column;border-radius:24px;' +
    'background:var(--surface);border:2px solid var(--line);box-shadow:var(--shadow);overflow:hidden}' +
    '.phead{display:flex;align-items:center;gap:4px;padding:8px 8px 8px 16px;background:var(--surface-2)}' +
    '.ptitle{flex:1;min-width:0;font:600 16px/1.2 var(--display);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}' +
    '.ptitle small{display:block;font:500 12px/1.3 inherit;color:var(--muted);letter-spacing:.04em}' +
    '.icon{flex:none;width:38px;height:38px;border-radius:50%;display:grid;place-items:center}.icon:hover{background:var(--surface-3)}.icon svg{width:18px;height:18px}' +
    '.chips{flex:none;display:flex;flex-wrap:wrap;gap:6px;max-height:84px;overflow-y:auto;padding:10px 12px 4px}' +
    '.chip{flex:none;display:inline-flex;align-items:center;gap:6px;min-height:32px;max-width:150px;padding:0 12px 0 5px;border-radius:999px;background:var(--surface-2);font-size:13px;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}' +
    '.chip.me{cursor:default}.chip.watcher{background:transparent;border:2px dashed var(--line)}.chip[aria-pressed="true"]{text-decoration:line-through;opacity:.55}' +
    '.log{flex:1 1 auto;min-height:56px;overflow-y:auto;padding:8px 12px 10px;display:flex;flex-direction:column;gap:8px;font-size:15px;overscroll-behavior:contain;word-wrap:break-word;overflow-wrap:anywhere}' +
    '.msg{display:flex;align-items:flex-end;gap:8px;max-width:92%}' +
    '.bubble{min-width:0;padding:7px 12px 8px;border-radius:18px 18px 18px 6px;background:var(--surface-2)}' +
    '.bubble b{display:block;font:600 12px/1.3 var(--display);letter-spacing:.02em}' +
    '.msg.mine{align-self:flex-end}.msg.mine .bubble{border-radius:18px 18px 6px 18px;background:var(--brand);color:var(--on-brand)}' +
    '.note{align-self:center;max-width:95%;padding:3px 12px;border-radius:999px;background:rgba(255,255,255,.06);color:var(--muted);font-size:12px;text-align:center}' +
    '.quick{display:flex;gap:6px;overflow-x:auto;padding:0 12px 8px;scrollbar-width:none}.quick::-webkit-scrollbar{display:none}' +
    '.quick button{flex:none;min-height:38px;padding:0 14px;border-radius:999px;background:var(--surface-2);border:2px solid var(--line);font-size:14px;font-weight:600;white-space:nowrap}' +
    '.quick button:active{background:var(--surface-3)}' +
    '.send{flex:none;display:flex;gap:6px;padding:8px;border-top:2px solid var(--line)}.send .field{min-height:44px}.send .btn{min-height:44px;padding:0 16px}' +
    '.phead,.quick{flex:none}' +
    // a phone on its side: the panel may use the height above the Hoppa badge, without the row of players (the title counts them)
    '@media (max-height:500px){.panel{max-height:calc(100vh - 72px);width:300px}.chips{display:none}.log{min-height:40px}.quick button{min-height:34px}}';

  var ICONS = {
    close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    chat: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linejoin="round"><path d="M4 5h16v11H9l-5 4z"/></svg>',
    share: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1"/></svg>',
    fold: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M6 12h12"/></svg>'
  };

  var QUICK_LINES = ['Hoi! 👋', 'Kom mee!', 'Wacht even', 'Goed zo! 🎉', 'Nog een keer?', 'GG'];

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function iconButton(className, icon, label) {
    var button = el('button', className);
    button.type = 'button';
    button.innerHTML = ICONS[icon]; // a constant above, never anything from a player
    button.setAttribute('aria-label', label);
    button.title = label;
    return button;
  }

  // Fredoka for the overlays: a font face from the page's head also reaches into the shadow root. Once,
  // and not when the game already loads it; without the network the system font takes over.
  var fontAsked = false;
  function ensureFont() {
    if (fontAsked) return;
    fontAsked = true;
    try {
      if (document.querySelector('link[href*="family=Fredoka"]')) return;
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = FONT_URL;
      (document.head || document.documentElement).appendChild(link);
    } catch (e) {
      /* no head: the system font */
    }
  }

  // A player's colour and initial, the same on every screen: from the player id
  function avatar(player, small) {
    var id = String(player.id || player.name || '');
    var hash = 0;
    for (var i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
    var node = el('span', small ? 'ava sm' : 'ava', Array.from(String(player.name || '?'))[0] || '?');
    node.style.background = PLAYER_COLORS[hash % PLAYER_COLORS.length];
    node.setAttribute('aria-hidden', 'true');
    return node;
  }

  // A layer over the page: a fixed host with a shadow root, our styles in it, and events that stop there
  function uiLayer(kind) {
    ensureFont();
    var host = document.createElement('div');
    host.setAttribute('data-hoppa-ui', kind);
    host.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;z-index:2147483000;';
    var root = host.attachShadow ? host.attachShadow({ mode: 'open' }) : host;
    var style = document.createElement('style');
    style.textContent = UI_CSS;
    root.appendChild(style);
    var shell = el('div', 'hp');
    root.appendChild(shell);
    // keyup goes through: whoever runs and presses T (or clicks the chat) lets go of the arrow in the
    // field, and the game must hear that, or the figure keeps running
    [
      'keydown',
      'keypress',
      'pointerdown',
      'pointerup',
      'pointermove',
      'mousedown',
      'mouseup',
      'touchstart',
      'touchmove',
      'touchend',
      'click',
      'dblclick',
      'wheel',
      'contextmenu'
    ].forEach(function (type) {
      host.addEventListener(type, function (e) {
        e.stopPropagation();
      });
    });
    (document.body || document.documentElement).appendChild(host);
    return {
      shell: shell,
      remove: function () {
        if (host.parentNode) host.parentNode.removeChild(host);
      }
    };
  }

  function sinceText(iso) {
    var ms = Date.now() - Date.parse(iso);
    if (!(ms >= 0)) return '';
    var minutes = Math.floor(ms / 60000);
    if (minutes < 1) return 'net begonnen';
    if (minutes < 60) return 'sinds ' + minutes + ' min';
    return 'sinds ' + Math.floor(minutes / 60) + ' uur';
  }

  // Share a room link: the share sheet on a phone, the clipboard elsewhere → 'shared' | 'copied' | 'cancelled' | 'none'
  async function shareLink(url) {
    if (isPhone()) {
      try {
        await navigator.share({ title: document.title, url: url });
        return 'shared';
      } catch (e) {
        if (e && e.name === 'AbortError') return 'cancelled';
      }
    }
    return (await copyText(url)) ? 'copied' : 'none';
  }

  function isCoarse() {
    try {
      return matchMedia('(pointer: coarse)').matches;
    } catch (e) {
      return false;
    }
  }

  // ── the chat panel: room.showChat() ──

  function createChatPanel(room, opts) {
    var corner = { 'top-left': 'tl', 'bottom-left': 'bl', 'bottom-right': 'br' }[opts.corner] || 'tr';
    var layer = uiLayer('chat');
    var wrap = el('div', 'chat ' + corner);
    var toggle = iconButton('toggle', 'chat', 'Chat openen (T)');
    var badge = el('span', 'badge hidden');
    toggle.appendChild(badge);
    var toast = el('div', 'toast hidden');
    toast.setAttribute('aria-hidden', 'true');
    var panel = el('section', 'panel');
    panel.setAttribute('aria-label', 'Chat');
    var head = el('div', 'phead');
    var title = el('span', 'ptitle');
    var shareButton = iconButton('icon', 'share', 'Deel de link van deze kamer');
    var foldButton = iconButton('icon', 'fold', 'Chat inklappen');
    head.appendChild(title);
    head.appendChild(shareButton);
    head.appendChild(foldButton);
    var chips = el('div', 'chips');
    var log = el('div', 'log');
    log.setAttribute('role', 'log');
    log.setAttribute('aria-live', 'polite');
    var quick = el('div', 'quick');
    var form = el('form', 'send');
    var input = el('input', 'field');
    input.type = 'text';
    input.placeholder = isCoarse() ? 'Zeg iets…' : 'Zeg iets… (T)'; // with a keyboard: T to type
    input.maxLength = 400; // cut to CHAT_MAX characters by room.chat; emoji are two units here
    input.setAttribute('aria-label', 'Chatbericht');
    input.setAttribute('enterkeyhint', 'send');
    input.autocomplete = 'off';
    var sendButton = el('button', 'btn primary', 'Stuur');
    sendButton.type = 'submit';
    form.appendChild(input);
    form.appendChild(sendButton);
    panel.appendChild(head);
    panel.appendChild(chips);
    panel.appendChild(log);
    if (isCoarse()) {
      QUICK_LINES.forEach(function (text) {
        var b = el('button', '', text);
        b.type = 'button';
        b.addEventListener('click', function () {
          say(text);
        });
        quick.appendChild(b);
      });
      panel.appendChild(quick);
    }
    panel.appendChild(form);
    wrap.appendChild(toggle);
    wrap.appendChild(toast);
    wrap.appendChild(panel);
    layer.shell.appendChild(wrap);

    var expanded = false;
    var unread = 0;
    var toastTimer = null;

    function setExpanded(on) {
      expanded = on;
      panel.classList.toggle('hidden', !on);
      toggle.classList.toggle('hidden', on);
      toggle.setAttribute('aria-expanded', on ? 'true' : 'false');
      if (on) {
        unread = 0;
        badge.classList.add('hidden');
        toast.classList.add('hidden');
        log.scrollTop = log.scrollHeight;
      }
    }

    function showToast(who, text) {
      if (expanded) return;
      toast.textContent = '';
      toast.classList.toggle('plain', !who);
      if (who) toast.appendChild(avatar(who, true));
      var body = el('span');
      if (who) body.appendChild(el('b', '', who.name + ' '));
      body.appendChild(document.createTextNode(text));
      toast.appendChild(body);
      toast.classList.remove('hidden');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function () {
        toast.classList.add('hidden');
      }, 4000);
    }

    function append(node) {
      var atBottom = log.scrollHeight - log.scrollTop - log.clientHeight < 40;
      log.appendChild(node);
      while (log.childNodes.length > 80) log.removeChild(log.firstChild);
      if (atBottom || expanded) log.scrollTop = log.scrollHeight;
    }

    function say(text) {
      if (room.chat(text)) return true;
      if (cleanChat(text)) note('Even wachten, niet zo snel.');
      return false;
    }

    function note(text) {
      append(el('div', 'note', text));
      showToast('', text);
      refresh();
    }

    function refresh() {
      var players = room.players;
      title.textContent = (room.isPrivate ? 'Privékamer ' : 'Kamer ') + (room.code || '…') + ' · ' + players.length + '/' + room.maxPlayers;
      chips.textContent = '';
      // Who watches from Hoppa Games (#448) has a dashed chip and can be muted like a player
      players.concat(room.watchers).forEach(function (p) {
        var mine = p.id === room.me.id;
        var chip = el('button', mine ? 'chip me' : p.watcher ? 'chip watcher' : 'chip', mine ? p.name + ' (jij)' : p.name);
        chip.insertBefore(avatar(p, true), chip.firstChild);
        chip.type = 'button';
        if (mine) {
          chip.disabled = true;
        } else {
          var isMuted = room.isMuted(p);
          chip.setAttribute('aria-pressed', isMuted ? 'true' : 'false');
          chip.title = (p.watcher ? p.name + ' kijkt mee op Hoppa Games. ' : '') + (isMuted ? p.name + ' weer horen' : p.name + ' dempen');
          chip.addEventListener('click', function () {
            var now = !room.isMuted(p);
            room.mute(p, now);
            append(el('div', 'note', now ? 'Je ziet de chat van ' + p.name + ' niet meer.' : 'Je ziet de chat van ' + p.name + ' weer.'));
          });
        }
        chips.appendChild(chip);
      });
    }

    toggle.addEventListener('click', function () {
      setExpanded(true);
    });
    foldButton.addEventListener('click', function () {
      setExpanded(false);
    });
    shareButton.addEventListener('click', async function () {
      if (!room.link) return;
      var how = await shareLink(room.link);
      if (how === 'copied') note('Link gekopieerd: stuur hem naar een vriend.');
      else if (how === 'none') note('Deel deze link: ' + room.link);
    });
    // T to type (the human on #468, as in Minecraft): the panel opens with the cursor in the field; Enter
    // sends and Esc does not, both go straight back to the game, with the panel as it was. Not while the
    // player types somewhere else (a name for the leaderboard), not with Ctrl/Alt/Cmd, not when hidden.
    // On window in the capture phase, so the game never sees this T.
    var byKey = null; // { wasExpanded } while typing after T
    function onShortcut(e) {
      if (String(e.key || '').toLowerCase() !== 't' || e.ctrlKey || e.metaKey || e.altKey || e.repeat || e.isComposing) return;
      if (wrap.classList.contains('hidden')) return;
      if (isEditable(e.composedPath ? e.composedPath()[0] : e.target)) return;
      e.preventDefault(); // no "t" in the field
      e.stopPropagation();
      byKey = { wasExpanded: expanded };
      setExpanded(true);
      input.focus();
    }
    function backToGame() {
      var was = byKey;
      byKey = null;
      input.blur();
      if (was && !was.wasExpanded) setExpanded(false);
    }
    window.addEventListener('keydown', onShortcut, true);
    input.addEventListener('blur', function () {
      byKey = null; // clicked on the game: the panel stays as it is
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (say(input.value)) input.value = '';
      else if (input.value.trim()) return; // too fast: the line stays, so it can go a moment later
      if (byKey) backToGame();
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        if (byKey) return backToGame();
        input.blur();
        setExpanded(false);
      }
    });

    refresh();
    // Wide screens have room for the open panel; a phone starts with the button, new lines show as a toast
    var wide = false;
    try {
      wide = matchMedia('(min-width: 720px)').matches;
    } catch (e) {
      /* no matchMedia */
    }
    setExpanded(opts.open != null ? !!opts.open : wide);

    // A bubble: yours on the right in yellow, the others on the left with their colour and initial.
    // quiet: a line from before (F5, a reconnect) — no toast, no unread count
    function line(player, text, quiet) {
      var mine = player.id === room.me.id;
      var row = el('div', mine ? 'msg mine' : 'msg');
      var bubble = el('div', 'bubble');
      if (!mine) {
        row.appendChild(avatar(player));
        bubble.appendChild(el('b', '', player.watcher ? player.name + ' · kijkt mee' : player.name));
      }
      bubble.appendChild(document.createTextNode(text));
      row.appendChild(bubble);
      append(row);
      if (!expanded && !mine && !quiet) {
        unread++;
        badge.textContent = unread > 9 ? '9+' : String(unread);
        badge.classList.remove('hidden');
        showToast(player, text);
      }
    }

    // What was said before this panel (or this page: F5) came
    room.chatLog.forEach(function (l) {
      line(l.player, l.text, true);
    });
    log.scrollTop = log.scrollHeight;

    return {
      line: line,
      note: note,
      refresh: refresh,
      show: function () {
        wrap.classList.remove('hidden');
      },
      hide: function () {
        wrap.classList.add('hidden');
      },
      destroy: function () {
        clearTimeout(toastTimer);
        window.removeEventListener('keydown', onShortcut, true);
        layer.remove();
      }
    };
  }

  // ── the lobby: hoppa.rooms.lobby() ──

  // The list shares the store's per-IP window (120 a minute) with the ticket and hoppa.store: a class of
  // eight behind one address with the lobby open asks 48 a minute at most, so Meedoen still gets its ticket
  // (review #468). After a failed refresh (429, offline) the wait doubles, up to a minute.
  var LOBBY_FAST = 10000; // refresh the open rooms this often while the lobby is new
  var LOBBY_SLOW = 20000; // and after three minutes this often
  var LOBBY_BACKOFF_MAX = 60000;
  var lobbyOpen = null; // the promise while the lobby is on screen
  var lobbyRoom = null; // the room the lobby gave, as long as it is open

  function lobbyRequested() {
    try {
      var params = new URLSearchParams(location.search);
      if (params.get('lobby') === '1') return true;
    } catch (e) {
      return false;
    }
    // F5 in a room (#448): the game calls lobby() at load, and lobby() takes the player straight back in
    return !!hoppa.rooms.linkCode || !!rememberedRoom();
  }

  async function listRooms() {
    var data = await request('GET', '/rooms');
    return (data.rooms || []).map(function (r) {
      return {
        code: String(r.code),
        host: String(r.host || 'Speler'),
        players: Number(r.players) || 0,
        maxPlayers: Number(r.maxPlayers) || 4,
        since: String(r.since || '')
      };
    });
  }

  function joinError(e) {
    switch (e && e.code) {
      case 'room_gone':
        return 'Deze kamer bestaat niet (meer). Kies een andere of maak een nieuwe.';
      case 'room_full':
        return 'Deze kamer is net vol. Kies een andere of maak een nieuwe.';
      case 'invalid_input':
        return 'Een code is vier letters, zonder I en O.';
      case 'rate_limited':
        return 'Het is even heel druk. Probeer het zo nog eens.';
      case 'room_limit':
        return e.message || 'Er kunnen nu geen kamers bij.';
      default:
        return 'Geen verbinding. Probeer het zo nog eens.';
    }
  }

  function showLobby(options, done) {
    var layer = uiLayer('lobby');
    var backdrop = el('div', 'backdrop');
    var card = el('div', 'card');
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
    card.setAttribute('aria-labelledby', 'hp-lobby-title');
    card.tabIndex = -1;
    backdrop.appendChild(card);
    layer.shell.appendChild(backdrop);

    var finished = false;
    var busy = false;
    var pollTimer = null;
    var failures = 0; // refreshes that failed in a row
    var openedAt = Date.now();
    var joined = null; // a private room we made: its code is on screen
    var buttons = [];

    function finish(room) {
      if (finished) return;
      finished = true;
      clearTimeout(pollTimer);
      document.removeEventListener('visibilitychange', onVisible);
      layer.remove();
      done(room);
    }

    // ── the first screen: name, open rooms, new room, a code ──
    var top = el('div', 'top');
    var heading = el('div');
    var h2 = el('h2', '', options.title || 'Samen spelen');
    h2.id = 'hp-lobby-title';
    heading.appendChild(h2);
    heading.appendChild(el('p', 'sub', document.title || ''));
    top.appendChild(heading);
    var closeButton = iconButton('close', 'close', 'Sluiten');
    top.appendChild(closeButton);
    card.appendChild(top);

    var linkCode = options.noInvite ? null : hoppa.rooms.linkCode;
    if (linkCode) {
      var invite = el('div', 'invite');
      invite.appendChild(el('p', '', 'Je bent uitgenodigd voor kamer ' + linkCode));
      var inviteButton = el('button', 'btn primary', 'Doe mee');
      inviteButton.type = 'button';
      inviteButton.addEventListener('click', function () {
        enter({ code: linkCode });
      });
      buttons.push(inviteButton);
      invite.appendChild(inviteButton);
      card.appendChild(invite);
    }

    var nameSection = el('div', 'section');
    var nameLabel = el('label', 'label', 'Jouw naam');
    nameLabel.htmlFor = 'hp-lobby-name';
    var nameInput = el('input', 'field');
    nameInput.id = 'hp-lobby-name';
    nameInput.maxLength = 16;
    nameInput.autocomplete = 'nickname';
    nameInput.placeholder = 'Bijnaam';
    nameInput.value = cleanName(options.name != null ? options.name : rememberedNickname());
    nameSection.appendChild(nameLabel);
    nameSection.appendChild(nameInput);
    card.appendChild(nameSection);

    var roomsSection = el('div', 'section');
    roomsSection.appendChild(el('p', 'label', 'Open kamers'));
    var list = el('ul', 'rooms');
    list.setAttribute('aria-live', 'polite');
    var empty = el('p', 'empty', 'Kamers zoeken…');
    roomsSection.appendChild(list);
    roomsSection.appendChild(empty);
    card.appendChild(roomsSection);

    var newSection = el('div', 'section');
    var newButton = el('button', 'btn primary', 'Nieuwe kamer');
    newButton.type = 'button';
    newButton.style.width = '100%';
    buttons.push(newButton);
    var privateLabel = el('label', 'check');
    var privateBox = el('input');
    privateBox.type = 'checkbox';
    privateLabel.appendChild(privateBox);
    privateLabel.appendChild(document.createTextNode('Privé: alleen wie de code heeft'));
    newSection.appendChild(newButton);
    newSection.appendChild(privateLabel);
    card.appendChild(newSection);

    var codeSection = el('div', 'section');
    var codeLabel = el('label', 'label', 'Heb je een code?');
    codeLabel.htmlFor = 'hp-lobby-code';
    var codeRow = el('form', 'row');
    var codeInput = el('input', 'field code');
    codeInput.id = 'hp-lobby-code';
    codeInput.maxLength = 4;
    codeInput.placeholder = 'ABCD';
    codeInput.autocomplete = 'off';
    codeInput.setAttribute('autocapitalize', 'characters');
    codeInput.setAttribute('enterkeyhint', 'go');
    var codeButton = el('button', 'btn ghost', 'Doe mee');
    codeButton.type = 'submit';
    buttons.push(codeButton);
    codeRow.appendChild(codeInput);
    codeRow.appendChild(codeButton);
    codeSection.appendChild(codeLabel);
    codeSection.appendChild(codeRow);
    card.appendChild(codeSection);

    var error = el('p', 'error');
    error.setAttribute('role', 'alert');
    card.appendChild(error);
    var soloButton = el('button', 'solo', 'Alleen spelen');
    soloButton.type = 'button';
    card.appendChild(soloButton);

    function setBusy(on) {
      busy = on;
      buttons.forEach(function (b) {
        b.disabled = on;
      });
    }

    function renderRooms(rooms) {
      list.textContent = '';
      rooms.forEach(function (r) {
        var item = el('li', 'room');
        var who = el('div', 'who');
        who.appendChild(el('strong', '', 'Kamer van ' + r.host));
        var since = sinceText(r.since);
        who.appendChild(el('span', 'meta', r.players + '/' + r.maxPlayers + ' spelers' + (since ? ' · ' + since : '')));
        var join = el('button', 'btn primary', 'Meedoen');
        join.type = 'button';
        join.disabled = busy;
        join.setAttribute('aria-label', 'Meedoen met de kamer van ' + r.host);
        join.addEventListener('click', function () {
          enter({ code: r.code });
        });
        buttons.push(join);
        item.appendChild(who);
        item.appendChild(join);
        list.appendChild(item);
      });
      empty.textContent = 'Nog geen open kamers. Maak er een, dan kan iemand anders meedoen.';
      empty.classList.toggle('hidden', rooms.length > 0);
    }

    async function refreshRooms() {
      clearTimeout(pollTimer);
      if (finished || joined) return;
      try {
        var rooms = await listRooms();
        if (finished || joined) return;
        buttons = buttons.filter(function (b) {
          return list.contains(b) === false;
        });
        renderRooms(rooms);
        failures = 0;
      } catch (e) {
        if (finished || joined) return;
        failures++;
        if (!list.firstChild) empty.textContent = 'De kamers konden niet geladen worden. Je kunt wel een nieuwe maken.';
      }
      schedule();
    }

    function schedule() {
      clearTimeout(pollTimer);
      if (finished || joined || document.visibilityState === 'hidden') return;
      var wait = Date.now() - openedAt < 180000 ? LOBBY_FAST : LOBBY_SLOW;
      if (failures > 0) wait = Math.min(LOBBY_BACKOFF_MAX, wait * Math.pow(2, failures));
      pollTimer = setTimeout(refreshRooms, wait);
    }

    function onVisible() {
      if (document.visibilityState === 'visible') refreshRooms();
    }
    document.addEventListener('visibilitychange', onVisible);

    async function enter(how) {
      if (busy || finished) return;
      var name = cleanName(nameInput.value);
      if (!name) {
        error.textContent = 'Kies eerst een naam.';
        nameInput.focus();
        return;
      }
      error.textContent = '';
      rememberNickname(name);
      nameInput.value = name;
      setBusy(true);
      try {
        // code '' is "a new room", also on a page whose link carries ?room=
        var room = await createRoom(how.code ? { code: how.code, name: name, existing: true } : { code: '', name: name, private: !!how.private });
        if (finished) return room.close();
        if (room.isPrivate && room.isHost) showCode(room);
        else finish(room);
      } catch (e) {
        error.textContent = joinError(e);
        refreshRooms();
      } finally {
        setBusy(false);
      }
    }

    // ── a private room: show the code to pass on, then play ──
    function showCode(room) {
      joined = room;
      clearTimeout(pollTimer);
      while (card.childNodes.length > 1) card.removeChild(card.lastChild); // keep the top bar
      h2.textContent = 'Je kamer is klaar';
      var body = el('div', 'stack');
      body.appendChild(el('p', 'lead', 'Alleen wie de code of de link heeft, kan meedoen.'));
      var big = el('p', 'bigcode', room.code);
      big.setAttribute('aria-label', 'Code ' + room.code.split('').join(' '));
      body.appendChild(big);
      var shareButton = el('button', 'btn ghost', 'Deel de link');
      shareButton.type = 'button';
      var shared = el('p', 'meta');
      shareButton.addEventListener('click', async function () {
        var how = await shareLink(room.link);
        shared.textContent = how === 'copied' ? 'Link gekopieerd.' : how === 'none' ? room.link : '';
      });
      var playButton = el('button', 'btn primary', 'Spelen');
      playButton.type = 'button';
      playButton.addEventListener('click', function () {
        finish(room);
      });
      body.appendChild(shareButton);
      body.appendChild(shared);
      body.appendChild(playButton);
      card.appendChild(body);
      playButton.focus();
    }

    newButton.addEventListener('click', function () {
      enter({ private: privateBox.checked });
    });
    codeRow.addEventListener('submit', function (e) {
      e.preventDefault();
      var typed = codeInput.value.replace(/\s+/g, '').toUpperCase();
      if (!ROOM_CODE.test(typed)) {
        error.textContent = 'Een code is vier letters, zonder I en O.';
        codeInput.focus();
        return;
      }
      enter({ code: typed });
    });
    codeInput.addEventListener('input', function () {
      codeInput.value = codeInput.value.toUpperCase();
    });
    // Closing is playing alone; with a private room made it is playing in that room
    closeButton.addEventListener('click', function () {
      finish(joined);
    });
    soloButton.addEventListener('click', function () {
      finish(null);
    });
    backdrop.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') finish(joined);
    });

    // Coming from Hoppa Games into a room that is gone or full: say why, the open rooms are below
    if (options.error) error.textContent = options.error;
    card.focus();
    refreshRooms();
  }

  var hoppa = {
    get sid() {
      return sid();
    },
    get visitorId() {
      return visitorId();
    },

    ai: {
      call: async function (name, input) {
        var data = await request('POST', '/ai/' + enc(name), input || {});
        return data.result;
      }
    },

    store: {
      get: async function (ns, key, options) {
        var data = await request('GET', '/data/' + enc(ns) + '/' + enc(key) + scopeQuery(options));
        return data.value;
      },
      set: async function (ns, key, value, options) {
        await request('PUT', '/data/' + enc(ns) + '/' + enc(key) + scopeQuery(options), value === undefined ? null : value);
      },
      increment: async function (ns, key, by, options) {
        var data = await request('POST', '/data/' + enc(ns) + '/' + enc(key) + '/increment' + scopeQuery(options), {
          by: by == null ? 1 : by
        });
        return data.value;
      },
      list: async function (ns, options) {
        options = options || {};
        var url = '/data/' + enc(ns) + scopeQuery(options) + '&top=' + enc(options.top || 10) + '&order=' + enc(options.order || 'desc');
        var data = await request('GET', url);
        return data.items;
      }
    },

    // A leaderboard per board name ('punten', 'level-2'). Per player (this browser) only the best
    // score counts; a board is highest-wins unless its scores go in with { lowestWins: true }.
    scores: {
      get nickname() {
        return rememberedNickname();
      },
      set nickname(name) {
        rememberNickname(name);
      },
      // → { newRecord, best, rank, total, name, nameReplaced }; rank is null when a full board had no room
      submit: async function (board, name, score, options) {
        if (name == null || String(name).trim() === '') name = rememberedNickname();
        var data = await request('POST', '/scores/' + enc(board), {
          name: String(name),
          score: Number(score),
          lowestWins: !!(options && options.lowestWins)
        });
        rememberNickname(data.name);
        return data;
      },
      // → [{ rank, name, score, me }], best first; me is true on this player's own line
      top: async function (board, count) {
        var data = await request('GET', '/scores/' + enc(board) + '?top=' + enc(count || 10));
        return data.items;
      },

      // The challenge this page was opened with → { name, score, board, lowestWins, outcome(myScore) }
      // where outcome is 'won' | 'lost' | 'tie'; null without one. Never on a leaderboard.
      get challenge() {
        return readChallenge();
      },

      // Challenge a friend: puts the score on the board (so the name passes the same filter), then
      // shares a link to this page with name and score — the share sheet on a phone, the clipboard
      // on a desktop. → { how: 'shared' | 'copied' | 'cancelled' | 'none', url, name, text };
      // with 'none' the page shows url itself so it can be copied by hand.
      shareChallenge: async function (board, name, score, options) {
        options = options || {};
        if (name == null || String(name).trim() === '') name = rememberedNickname();
        score = Number(score);
        if (!isFinite(score)) throw Object.assign(new Error('Geen geldige score'), { code: 'invalid_input' });
        if (!cleanName(name)) throw Object.assign(new Error('Vul eerst een bijnaam in'), { code: 'invalid_input' });
        try {
          name = (await hoppa.scores.submit(board, name, score, options)).name;
        } catch (e) {
          // Only offline or throttled: the link still works, with the name cut back as on arrival.
          // What the board refuses (another direction, a bad board or score) is no challenge either.
          if (e.code !== 'unavailable' && e.code !== 'rate_limited') throw e;
          name = cleanName(name);
        }

        var url = challengeUrl(board, name, score, !!options.lowestWins);
        var text = options.text || name + ' daagt je uit: versla ' + score + ' in ' + (document.title || 'dit spel') + '!';
        var result = { how: 'none', url: url, name: name, text: text };
        if (isPhone()) {
          try {
            await navigator.share({ title: document.title, text: text, url: url });
            result.how = 'shared';
            return result;
          } catch (e) {
            if (e && e.name === 'AbortError') {
              result.how = 'cancelled';
              return result;
            }
            // Not allowed here (an iframe without web-share): copy instead
          }
        }
        if (await copyText(text + ' ' + url)) result.how = 'copied';
        return result;
      }
    },

    // The Google reviews of the business linked with set_google_place (#523), refreshed once a day on
    // the server. → { name, rating, count, url, reviewsUrl, writeReviewUrl, reviews: [{ author, photo,
    // profileUrl, rating, text, date, relativeDate, reviewUrl }] }, newest first, at most 3. Never throws: without
    // a linked business, offline or throttled the list is empty, so the page simply hides its section.
    reviews: async function () {
      try {
        var data = await request('GET', '/reviews');
        if (data && Array.isArray(data.reviews)) return data;
      } catch (e) {
        /* the section stays hidden; a limit is already logged by request() */
      }
      return { name: null, rating: null, count: 0, url: null, reviewsUrl: null, writeReviewUrl: null, reviews: [] };
    },

    // Play together, live (#413). open() hosts a new room, or joins the code in ?room= of this page's
    // link, or { code } typed in; { name } is the nickname (default: the one this browser used last);
    // { private: true } keeps a new room out of the lobby (#448: rooms are open by default);
    // { code, existing: true } only joins: a code nobody has rejects with 'room_gone' (the lobby does this).
    // → a room: code, link, isHost, me, players, track(fn), onPlayer(cb), emit/on, onJoin/onLeave/onHost,
    // chat/onChat/showChat/hideChat/chatLog (#448), watchers (who reads along on Hoppa Games), close().
    // Rejects with code 'room_full', 'room_gone', 'room_limit', 'invalid_input' or 'unavailable'.
    rooms: {
      open: function (options) {
        try {
          return createRoom(options);
        } catch (e) {
          return Promise.reject(e);
        }
      },
      // The lobby (#448): an overlay with a nickname, the open rooms of this game, "Nieuwe kamer" and a
      // field for a code. → the room you are in, or null when the player chose to play alone. Already in
      // a room from the lobby: that room. { name, title } are optional.
      // From Hoppa Games (?room=&naam=) the lobby joins that room without its screen; only when that fails
      // (the room is gone or full) does it show, with the reason.
      lobby: function (options) {
        if (lobbyRoom && !lobbyRoom.closed) return Promise.resolve(lobbyRoom);
        if (lobbyOpen) return lobbyOpen;
        options = options || {};
        lobbyOpen = new Promise(function (resolve) {
          function done(room) {
            lobbyOpen = null;
            lobbyRoom = room;
            resolve(room);
          }
          var code = hoppa.rooms.linkCode;
          var memory = rememberedRoom();
          if (fromPlatform && code) {
            fromPlatform = false; // once: a later lobby() is the normal one
            createRoom({ code: code, name: rememberedNickname(), existing: true }).then(done, function (e) {
              forgetRoom(code);
              showLobby(Object.assign({}, options, { error: joinError(e), noInvite: true }), done);
            });
          } else if (memory && (!code || code === memory.code)) {
            // F5 in a room: straight back in, as the same player, with the chat. Gone in the meantime
            // (everyone left two minutes ago): the lobby as always
            createRoom({ code: memory.code, name: rememberedNickname(), existing: true }).then(done, function () {
              forgetRoom(memory.code);
              showLobby(options, done);
            });
          } else {
            showLobby(options, done);
          }
        });
        return lobbyOpen;
      },
      // The open rooms of this game for a lobby of your own → [{ code, host, players, maxPlayers, since }]
      list: listRooms,
      // The link asks for the lobby: ?lobby=1 (from the gallery of Hoppa Games), a ?room= invitation, or F5 in a room
      get lobbyRequested() {
        return lobbyRequested();
      },
      // The code in this page's link (?room=KIP4), or null: for a "Doe mee met KIP4" button
      get linkCode() {
        var raw = roomCodeFromLink();
        if (!raw) return null;
        var code = String(raw).replace(/\s+/g, '').toUpperCase();
        return ROOM_CODE.test(code) ? code : null;
      }
    }
  };

  // The stars of a game in Hoppa Games (#411): one vote of 1–5 per browser, changeable.
  // → { count, average, mine } (average and mine null when there are none). Deliberately not on
  // window.hoppa: the page shares this origin and visitor id, so a game that could call it would vote
  // 5 for every player. Only the panel below uses it, and only on a click the player made.
  var rating = {
    get: function () {
      return requestUrl('GET', '/api/games/' + sid() + '/rating');
    },
    set: function (stars) {
      return requestUrl('POST', '/api/games/' + sid() + '/rating', { stars: Number(stars) });
    }
  };

  // ── The star panel behind "☆ Beoordeel" in the badge of a published game (SessionFileServer) ──
  // Built here and not in the badge itself: the badge is inline HTML on every page, this runs once.
  // Everything is inline-styled and stops its events, so the game under it neither restyles the
  // panel nor reacts to a click or a key meant for it. A click from a script (el.click(),
  // dispatchEvent) is not trusted and casts no vote.
  function mountRatingPanel(button) {
    var panel = null;
    var state = null; // { count, average, mine }
    var busy = false;

    function el(tag, style, text) {
      var e = document.createElement(tag);
      if (style) e.style.cssText = style;
      if (text != null) e.textContent = text;
      return e;
    }
    function fmt(n) {
      return n.toLocaleString('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    }
    function summary() {
      if (!state || !state.count) return 'Nog geen sterren. Jij bent de eerste!';
      return '★ ' + fmt(state.average) + ' · ' + state.count + (state.count === 1 ? ' stem' : ' stemmen');
    }

    function render(message) {
      if (!panel) return;
      var stars = panel.querySelectorAll('[data-star]');
      var mine = state && state.mine;
      for (var i = 0; i < stars.length; i++) {
        var on = mine != null && i < mine;
        stars[i].textContent = on ? '★' : '☆';
        stars[i].style.color = on ? '#ffd23f' : '#fff';
        stars[i].setAttribute('aria-pressed', mine === i + 1 ? 'true' : 'false');
      }
      panel.querySelector('[data-summary]').textContent = summary();
      panel.querySelector('[data-note]').textContent =
        message || (mine != null ? 'Jij gaf ' + mine + (mine === 1 ? ' ster' : ' sterren') + '. Tik om te veranderen.' : '');
    }

    function build() {
      panel = el(
        'div',
        'position:fixed;right:16px;bottom:56px;z-index:2147483647;width:240px;max-width:calc(100vw - 32px);' +
          'box-sizing:border-box;padding:14px 16px;border-radius:18px;background:rgba(30,21,56,0.96);color:#fff;' +
          'font:14px/1.4 system-ui,sans-serif;text-align:left;box-shadow:0 10px 30px rgba(0,0,0,0.35);'
      );
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-label', 'Beoordeel dit spel');
      panel.setAttribute('data-hoppa-rating-panel', '');

      var head = el('div', 'display:flex;align-items:center;justify-content:space-between;gap:8px;');
      head.appendChild(el('strong', 'font-size:15px;', 'Wat vind je van dit spel?'));
      var close = el('button', 'background:none;border:0;color:#fff;font-size:20px;line-height:1;cursor:pointer;padding:2px 4px;', '×');
      close.type = 'button';
      close.setAttribute('aria-label', 'Sluiten');
      close.addEventListener('click', hide);
      head.appendChild(close);
      panel.appendChild(head);

      var row = el('div', 'display:flex;gap:2px;margin:8px 0 4px;');
      for (var i = 1; i <= 5; i++) {
        var star = el(
          'button',
          'background:none;border:0;padding:2px;font-size:30px;line-height:1;cursor:pointer;color:#fff;',
          '☆'
        );
        star.type = 'button';
        star.setAttribute('data-star', String(i));
        star.setAttribute('aria-label', i + (i === 1 ? ' ster' : ' sterren'));
        star.addEventListener('click', function (stars, e) {
          // Only the player's own click or key (Enter, space) counts, not one the game's script makes
          if (e.isTrusted) vote(stars);
        }.bind(null, i));
        row.appendChild(star);
      }
      panel.appendChild(row);
      panel.appendChild(el('div', 'font-weight:600;', '')).setAttribute('data-summary', '');
      panel.appendChild(el('div', 'opacity:0.8;font-size:12px;min-height:17px;margin-top:2px;', '')).setAttribute('data-note', '');

      ['click', 'pointerdown', 'pointerup', 'mousedown', 'mouseup', 'touchstart', 'touchend', 'keydown', 'keyup', 'wheel'].forEach(function (type) {
        panel.addEventListener(type, function (e) {
          e.stopPropagation();
          if (type === 'keydown' && e.key === 'Escape') hide();
        });
      });
      document.body.appendChild(panel);
    }

    async function load() {
      render('Even kijken…');
      try {
        state = await rating.get();
        render();
      } catch (e) {
        render(e.code === 'rate_limited' ? 'Even wachten, probeer het zo nog eens.' : 'De sterren konden niet geladen worden.');
      }
    }

    async function vote(stars) {
      if (busy) return;
      busy = true;
      try {
        state = await rating.set(stars);
        render('Bedankt! Jij gaf ' + stars + (stars === 1 ? ' ster' : ' sterren') + '.');
      } catch (e) {
        render(e.code === 'rate_limited' ? 'Even wachten, probeer het zo nog eens.' : 'Dat lukte niet. Probeer het opnieuw.');
      } finally {
        busy = false;
      }
    }

    function show() {
      if (!panel) build();
      panel.style.display = 'block';
      button.setAttribute('aria-expanded', 'true');
      var first = panel.querySelector('[data-star]');
      if (first) first.focus();
      load();
    }
    function hide() {
      if (panel) panel.style.display = 'none';
      button.setAttribute('aria-expanded', 'false');
    }

    button.setAttribute('aria-expanded', 'false');
    button.addEventListener('click', function (e) {
      e.stopPropagation();
      if (!e.isTrusted) return;
      if (panel && panel.style.display !== 'none') hide();
      else show();
    });
  }

  // ── typing in a field (#509) ──
  // A game that registers its keys with Phaser (addKeys('W,A,S,D'), the arrows) listens on window and
  // calls preventDefault on every keydown of those keys, also while the player types a name in an
  // HTML field: the a and the d never reach the field. A typing key from a field therefore stops at
  // document, for every game and without the model knowing. Escape, Enter and Tab go on (an "Escape
  // closes the screen" on window keeps working), and keyup always goes on: an arrow let go in the
  // field must stop the figure. Listeners of the game on document still get the key. Only a field the
  // player can see: a typing game that focuses a hidden field to bring up the phone's keyboard reads the
  // letters on window, and there nobody misses them in the field (review #519).
  function isEditable(node) {
    if (!node || node.nodeType !== 1) return false;
    var tag = node.tagName;
    if (tag === 'TEXTAREA' || tag === 'SELECT' || node.isContentEditable) return true;
    return tag === 'INPUT' && !/^(button|submit|reset|checkbox|radio|range|color|file|image|hidden)$/i.test(node.type || '');
  }
  function isTyping(key) {
    key = String(key || '');
    return key.length === 1 || /^(Backspace|Delete|ArrowLeft|ArrowRight|ArrowUp|ArrowDown|Home|End)$/.test(key);
  }
  function isSeen(node) {
    // opacity 0, visibility hidden or display none, on the field or a parent
    if (node.checkVisibility && !node.checkVisibility({ opacityProperty: true, visibilityProperty: true })) return false;
    if (!node.getBoundingClientRect) return true;
    var r = node.getBoundingClientRect();
    if (r.width < 4 || r.height < 4) return false; // the 1 px field of a screen reader or a keyboard trick
    var w = window.innerWidth;
    var h = window.innerHeight;
    return !(typeof w === 'number' && typeof h === 'number' && (r.right <= 0 || r.bottom <= 0 || r.left >= w || r.top >= h));
  }
  document.addEventListener('keydown', function (e) {
    var target = e.composedPath ? e.composedPath()[0] : e.target;
    if (isEditable(target) && isTyping(e.key) && isSeen(target)) e.stopPropagation();
  });

  // ── The AI assistant of a site (#599) ──
  // The server puts its button in the badge's shadow root (AssistantButton.cs) when the site has one, with the
  // name, colour, greeting and the site's contact as data attributes. A tap opens a chat panel in a shadow root of
  // its own: the visitor asks, POST /assistant/ask answers from the text of the site as it is now. The conversation
  // lives in sessionStorage (this tab), the last turns go along for context. Not known: the panel says so, shows the
  // contact and lets the visitor leave the question with an e-mail address (POST /assistant/leave, a submission for
  // the maker). The buttons and texts follow the site's <html lang>; the model answers in the visitor's language.
  var ASSISTANT_TEXTS = {
    nl: {
      button: 'Stel een vraag', buttonLabel: 'Stel een vraag aan de AI-assistent', title: 'AI-assistent van {name}',
      note: 'Je praat met een AI. De antwoorden komen uit deze website.',
      greeting: 'Hoi! Stel je vraag over {name}, bijvoorbeeld over openingstijden, prijzen of hoe je een afspraak maakt.',
      placeholder: 'Typ je vraag…', send: 'Versturen', close: 'Sluiten', thinking: 'Even kijken…',
      contact: 'Neem contact op:', call: 'Bellen', whatsapp: 'WhatsApp', mail: 'Mailen',
      leave: 'Of laat je vraag achter, dan antwoordt {name} je per mail.', email: 'Je e-mailadres', leaveButton: 'Vraag achterlaten',
      left: 'Bedankt! {name} heeft je vraag gekregen en antwoordt je per mail.',
      daily: 'Ik heb vandaag al veel vragen gehad.', dailyLater: 'Ik heb vandaag al veel vragen gehad. Probeer het morgen nog eens.',
      wait: 'Even wachten: je stelt veel vragen vlak na elkaar. Probeer het zo nog eens.',
      error: 'Dat lukte even niet. Probeer het zo nog eens.', badEmail: 'Vul een geldig e-mailadres in.'
    },
    en: {
      button: 'Ask a question', buttonLabel: 'Ask the AI assistant a question', title: 'AI assistant of {name}',
      note: 'You are talking to an AI. The answers come from this website.',
      greeting: 'Hi! Ask your question about {name}, for example about opening hours, prices or how to book.',
      placeholder: 'Type your question…', send: 'Send', close: 'Close', thinking: 'Let me check…',
      contact: 'Get in touch:', call: 'Call', whatsapp: 'WhatsApp', mail: 'E-mail',
      leave: 'Or leave your question and {name} will answer you by e-mail.', email: 'Your e-mail address', leaveButton: 'Leave question',
      left: 'Thank you! {name} has received your question and will answer you by e-mail.',
      daily: 'I have had a lot of questions today.', dailyLater: 'I have had a lot of questions today. Please try again tomorrow.',
      wait: 'One moment: you are asking many questions in a row. Please try again shortly.',
      error: 'That did not work. Please try again shortly.', badEmail: 'Please enter a valid e-mail address.'
    }
  };

  var ASSISTANT_CSS =
    ':host{all:initial}' +
    '*{box-sizing:border-box}' +
    '.panel{position:fixed;right:16px;bottom:calc(16px + env(safe-area-inset-bottom,0px));z-index:2147483647;width:380px;max-width:calc(100vw - 32px);' +
    'height:min(580px,calc(100vh - 32px));display:flex;flex-direction:column;background:#fff;color:#1f2937;border-radius:18px;overflow:hidden;' +
    'box-shadow:0 16px 48px rgba(0,0,0,.28);font:15px/1.45 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;text-align:left}' +
    '.panel[hidden],[hidden]{display:none}' +
    '.head{display:flex;align-items:center;gap:10px;padding:14px 12px 14px 16px;background:var(--c);color:var(--t)}' +
    '.head h2{flex:1;margin:0;font-size:16px;font-weight:700;line-height:1.3}' +
    '.x{flex:none;width:40px;height:40px;border:0;border-radius:50%;background:transparent;color:inherit;font-size:24px;line-height:1;cursor:pointer}' +
    '.x:hover{background:rgba(127,127,127,.18)}' +
    '.x:focus-visible,.send:focus-visible,.leave button:focus-visible{outline:3px solid #1f2937;outline-offset:2px}' +
    '.head .x:focus-visible{outline-color:var(--t)}' +
    '.note{margin:0;padding:7px 16px;font-size:12px;color:#374151;background:#f3f4f6;border-bottom:1px solid #e5e7eb}' +
    '.log{flex:1;overflow-y:auto;padding:14px;display:flex;flex-direction:column;gap:10px;background:#fafafa;overscroll-behavior:contain}' +
    '.msg{max-width:86%;padding:9px 13px;border-radius:16px;white-space:pre-wrap;overflow-wrap:anywhere}' +
    '.me{align-self:flex-end;background:var(--c);color:var(--t);border-bottom-right-radius:5px}' +
    '.ai{align-self:flex-start;background:#fff;border:1px solid #e5e7eb;border-bottom-left-radius:5px}' +
    '.ai.busy{color:#4b5563;font-style:italic}' +
    '.help{align-self:stretch;padding:10px 12px;border:1px solid #e5e7eb;border-radius:14px;background:#fff;font-size:14px}' +
    '.help p{margin:0 0 6px}' +
    '.links{display:flex;flex-wrap:wrap;gap:6px;margin:0 0 8px}' +
    '.links a{display:inline-flex;align-items:center;min-height:36px;padding:0 14px;border:2px solid var(--c);border-radius:999px;color:#1f2937;' +
    'text-decoration:none;font-weight:600}' +
    '.links a:focus-visible{outline:3px solid #1f2937;outline-offset:2px}' +
    '.leave{display:flex;gap:6px;flex-wrap:wrap}' +
    '.leave input{flex:1 1 180px;min-width:0;min-height:40px;padding:0 12px;border:1px solid #9ca3af;border-radius:10px;font:inherit;color:#1f2937;background:#fff}' +
    '.leave button,.send{min-height:40px;padding:0 14px;border:0;border-radius:10px;background:var(--c);color:var(--t);font:inherit;font-weight:600;cursor:pointer}' +
    '.err{margin:6px 0 0;color:#b91c1c}' +
    '.ask{display:flex;gap:8px;padding:10px;border-top:1px solid #e5e7eb;background:#fff}' +
    '.ask textarea{flex:1;min-width:0;min-height:44px;max-height:120px;padding:10px 12px;border:1px solid #9ca3af;border-radius:12px;font:inherit;' +
    'color:#1f2937;background:#fff;resize:none}' +
    '.ask textarea:focus-visible,.leave input:focus-visible{outline:2px solid var(--c);outline-offset:1px;border-color:#1f2937}' +
    '.send{min-height:44px}' +
    '.send:disabled,.leave button:disabled{opacity:.6;cursor:default}' +
    '.sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}' +
    '@media (max-width:480px){.panel{right:0;left:0;bottom:0;width:100%;max-width:none;height:calc(100% - 40px);' +
    'height:calc(100dvh - 40px);border-radius:18px 18px 0 0}}' +
    '@media print{.panel{display:none}}';

  var ASSISTANT_KEEP = 30; // lines kept in sessionStorage
  var ASSISTANT_HISTORY = 6; // lines that go along with a question
  var assistantPanel = null;

  function assistantLang() {
    var lang = String(document.documentElement.getAttribute('lang') || 'nl').slice(0, 2).toLowerCase();
    return ASSISTANT_TEXTS[lang] ? lang : 'en';
  }

  function mountAssistantButton(button) {
    if (button.__hoppaAssistant) return;
    button.__hoppaAssistant = true;
    var t = ASSISTANT_TEXTS[assistantLang()];
    button.setAttribute('aria-label', t.buttonLabel);
    var label = button.querySelector('.hoppa-assistant-label');
    if (label) label.textContent = t.button;
    button.setAttribute('aria-expanded', 'false');
    button.addEventListener('click', function () {
      openAssistant(button);
    });
  }

  function assistantConfig(button) {
    var d = button.dataset || {};
    return {
      name: d.name || document.title || '',
      color: d.color || '#1f2937',
      textColor: d.textColor || '#ffffff',
      greeting: d.greeting || '',
      contact: { phone: d.phone || null, whatsApp: d.whatsapp || null, whatsAppText: d.whatsappText || null, email: d.email || null }
    };
  }

  function openAssistant(button) {
    if (!assistantPanel) assistantPanel = buildAssistant(assistantConfig(button));
    assistantPanel.open(button);
  }

  function buildAssistant(config) {
    var t = ASSISTANT_TEXTS[assistantLang()];
    var storageKey = 'hoppa_assistant_' + sid();
    var lines = loadLines();
    var contact = config.contact;
    var busy = false;
    var opener = null;

    function fill(text) {
      return String(text).split('{name}').join(config.name);
    }
    function node(tag, className, text) {
      var n = document.createElement(tag);
      if (className) n.className = className;
      if (text != null) n.textContent = text;
      return n;
    }
    function loadLines() {
      try {
        var raw = JSON.parse(sessionStorage.getItem(storageKey) || '[]');
        return Array.isArray(raw) ? raw.filter(function (l) { return l && typeof l.text === 'string'; }) : [];
      } catch (e) {
        return [];
      }
    }
    function saveLines() {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(lines.slice(-ASSISTANT_KEEP)));
      } catch (e) {
        /* storage blocked: the conversation lasts as long as the page */
      }
    }

    var host = document.createElement('div');
    host.setAttribute('data-hoppa-ui', 'assistant');
    host.setAttribute('data-hoppa-injected', ''); // the inline editor leaves it out on save, like the badge
    host.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;z-index:2147483647;';
    var root = host.attachShadow ? host.attachShadow({ mode: 'open' }) : host;
    var style = document.createElement('style');
    style.textContent = ASSISTANT_CSS;
    root.appendChild(style);

    var panel = node('div', 'panel');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'false');
    panel.style.setProperty('--c', config.color);
    panel.style.setProperty('--t', config.textColor);
    panel.hidden = true;
    var head = node('div', 'head');
    var title = node('h2', null, fill(t.title));
    title.id = 'hoppa-assistant-title';
    panel.setAttribute('aria-labelledby', title.id);
    var close = node('button', 'x', '×');
    close.type = 'button';
    close.setAttribute('aria-label', t.close);
    close.title = t.close;
    head.appendChild(title);
    head.appendChild(close);
    var note = node('p', 'note', t.note);
    var log = node('div', 'log');
    log.setAttribute('aria-live', 'polite');
    var form = node('form', 'ask');
    var inputLabel = node('label', 'sr', t.placeholder);
    var input = node('textarea');
    input.id = 'hoppa-assistant-question';
    inputLabel.setAttribute('for', input.id);
    input.rows = 1;
    input.maxLength = 500;
    input.placeholder = t.placeholder;
    var send = node('button', 'send', t.send);
    send.type = 'submit';
    form.appendChild(inputLabel);
    form.appendChild(input);
    form.appendChild(send);
    panel.appendChild(head);
    panel.appendChild(note);
    panel.appendChild(log);
    panel.appendChild(form);
    root.appendChild(panel);
    // Typing in the panel is not the page's business (a game's keys, a site's own shortcuts)
    ['keydown', 'keypress'].forEach(function (type) {
      host.addEventListener(type, function (e) {
        if (e.key === 'Escape' && type === 'keydown') hide();
        e.stopPropagation();
      });
    });
    (document.body || document.documentElement).appendChild(host);

    function contactLinks() {
      var links = node('div', 'links');
      function add(href, label, blank) {
        var a = node('a', null, label);
        a.href = href;
        if (blank) {
          a.target = '_blank';
          a.rel = 'noopener';
        }
        links.appendChild(a);
      }
      if (contact.phone) add('tel:+' + contact.phone, t.call);
      if (contact.whatsApp)
        add('https://wa.me/' + contact.whatsApp + (contact.whatsAppText ? '?text=' + encodeURIComponent(contact.whatsAppText) : ''), t.whatsapp, true);
      if (contact.email) add('mailto:' + contact.email, t.mail);
      return links.childNodes.length ? links : null;
    }

    // Under an answer that was not on the site, and under the daily limit: how to reach the business, and (not at the
    // daily limit) the question left with an e-mail address
    function helpBox(line, index) {
      var box = node('div', 'help');
      var links = contactLinks();
      if (links) {
        box.appendChild(node('p', null, t.contact));
        box.appendChild(links);
      }
      if (line.daily) return links ? box : null;
      if (line.left) {
        box.appendChild(node('p', null, fill(t.left)));
        return box;
      }
      var question = '';
      for (var i = index - 1; i >= 0; i--) {
        if (lines[i].role === 'visitor') {
          question = lines[i].text;
          break;
        }
      }
      if (!question) return links ? box : null;
      box.appendChild(node('p', null, fill(t.leave)));
      var leave = node('form', 'leave');
      var mailLabel = node('label', 'sr', t.email);
      var mail = node('input');
      mail.type = 'email';
      mail.autocomplete = 'email';
      mail.required = true;
      mail.placeholder = t.email;
      mail.id = 'hoppa-assistant-email-' + index;
      mailLabel.setAttribute('for', mail.id);
      var go = node('button', null, t.leaveButton);
      go.type = 'submit';
      var err = node('p', 'err');
      err.hidden = true;
      leave.appendChild(mailLabel);
      leave.appendChild(mail);
      leave.appendChild(go);
      box.appendChild(leave);
      box.appendChild(err);
      leave.addEventListener('submit', function (e) {
        e.preventDefault();
        var address = mail.value.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(address)) {
          err.textContent = t.badEmail;
          err.hidden = false;
          return;
        }
        go.disabled = true;
        request('POST', '/assistant/leave', { question: question, email: address })
          .then(function () {
            line.left = true;
            saveLines();
            render();
          })
          .catch(function (error) {
            go.disabled = false;
            err.textContent = error && error.code === 'invalid_email' ? t.badEmail : error && error.message ? error.message : t.error;
            err.hidden = false;
          });
      });
      return box;
    }

    function render() {
      log.textContent = '';
      log.appendChild(node('div', 'msg ai', config.greeting || fill(t.greeting)));
      lines.forEach(function (line, i) {
        log.appendChild(node('div', line.role === 'visitor' ? 'msg me' : 'msg ai', line.text));
        if (line.role === 'assistant' && (line.unknown || line.daily)) {
          var box = helpBox(line, i);
          if (box) log.appendChild(box);
        }
      });
      if (busy) log.appendChild(node('div', 'msg ai busy', t.thinking));
      log.scrollTop = log.scrollHeight;
    }

    function ask(question) {
      var history = lines.slice(-ASSISTANT_HISTORY).map(function (l) {
        return { role: l.role, text: l.text };
      });
      lines.push({ role: 'visitor', text: question });
      saveLines();
      busy = true;
      send.disabled = true;
      render();
      request('POST', '/assistant/ask', { question: question, history: history })
        .then(function (data) {
          if (data && data.contact) contact = data.contact;
          lines.push({ role: 'assistant', text: data.answer, unknown: !data.answered });
        })
        .catch(function (error) {
          var details = (error && error.details) || {};
          if (details.contact) contact = details.contact;
          if (error && error.code === 'rate_limited' && details.daily) {
            var hasContact = contact.phone || contact.whatsApp || contact.email;
            lines.push({ role: 'assistant', text: hasContact ? t.daily : t.dailyLater, daily: true });
          } else if (error && error.code === 'rate_limited') {
            lines.push({ role: 'assistant', text: t.wait, transient: true });
          } else {
            lines.push({ role: 'assistant', text: t.error, transient: true });
          }
        })
        .then(function () {
          busy = false;
          send.disabled = false;
          saveLines();
          render();
        });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var question = input.value.trim();
      if (!question || busy) return;
      input.value = '';
      ask(question);
    });
    input.addEventListener('keydown', function (e) {
      // Enter sends, Shift+Enter is a new line
      if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
        e.preventDefault();
        if (form.requestSubmit) form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { cancelable: true }));
      }
    });
    close.addEventListener('click', hide);

    function hide() {
      if (panel.hidden) return;
      panel.hidden = true;
      if (opener) {
        opener.setAttribute('aria-expanded', 'false');
        opener.focus();
      }
    }

    return {
      open: function (button) {
        opener = button;
        button.setAttribute('aria-expanded', 'true');
        panel.hidden = false;
        render();
        input.focus();
      }
    };
  }

  // ── The badge's shadow root (#651) ──
  // The server puts the badge and the contact button in <hoppa-badge> with a <template shadowrootmode>,
  // so the page's CSS cannot reach them. A browser without declarative shadow DOM (Firefox before 123)
  // keeps the template as an inert element: attach the shadow root here instead. Without attachShadow
  // at all the content goes into the page itself, styled by the page, but at least it is there.
  function attachDeclarativeShadows() {
    var templates = document.querySelectorAll('hoppa-badge > template[shadowrootmode]');
    for (var i = 0; i < templates.length; i++) {
      var template = templates[i];
      var host = template.parentNode;
      if (host.shadowRoot) continue;
      var root = host.attachShadow ? host.attachShadow({ mode: 'open' }) : host;
      root.appendChild(template.content);
      template.remove();
    }
  }

  // A page that re-parses its body (document.body.innerHTML += '<div class="toast">…') reads it through
  // the innerHTML getter, which leaves shadow roots out, and the setter makes no declarative shadow
  // roots: what is left is an empty <hoppa-badge>. Keep a copy of the shadow root's content and put it
  // back on a host that lost its shadow root (review #655); before #651 the badge was plain markup and
  // survived that.
  var badgeContent = null;

  function mountRatingButtonsIn(root) {
    var buttons = root.querySelectorAll('[data-hoppa-rate]');
    for (var i = 0; i < buttons.length; i++) mountRatingPanel(buttons[i]);
    // The AI assistant's button lives in the same shadow root (#599)
    var assistants = root.querySelectorAll('[data-hoppa-assistant]');
    for (var j = 0; j < assistants.length; j++) mountAssistantButton(assistants[j]);
  }

  function restoreBadges() {
    var hosts = document.querySelectorAll('hoppa-badge');
    for (var h = 0; h < hosts.length; h++) {
      var host = hosts[h];
      if (host.shadowRoot || !host.attachShadow) continue;
      var root = host.attachShadow({ mode: 'open' });
      for (var i = 0; i < badgeContent.length; i++) root.appendChild(badgeContent[i].cloneNode(true));
      mountRatingButtonsIn(root);
    }
  }

  // The rate button lives in the badge's shadow root; querySelectorAll on the document does not see it
  function mountRatingButtons() {
    attachDeclarativeShadows();
    mountRatingButtonsIn(document);
    var hosts = document.querySelectorAll('hoppa-badge');
    for (var h = 0; h < hosts.length; h++) {
      var root = hosts[h].shadowRoot;
      if (!root) continue;
      if (!badgeContent) badgeContent = Array.prototype.map.call(root.childNodes, function (node) { return node.cloneNode(true); });
      mountRatingButtonsIn(root);
    }
    // Only the body's own children: replacing the body's content is what loses the shadow root, and a
    // game that changes its DOM deeper down every frame does not wake this up
    if (badgeContent && typeof MutationObserver === 'function' && document.body)
      new MutationObserver(restoreBadges).observe(document.body, { childList: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mountRatingButtons);
  else mountRatingButtons();

  window.hoppa = hoppa;
  // Pages built before hoppa.js existed carry their own inline hoppaAi(); keep the name working
  if (!window.hoppaAi) window.hoppaAi = hoppa.ai.call;
})();
